import React, { useState, useEffect } from 'react'
import { apiService } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import * as XLSX from 'xlsx'
import { 
  CalculatorIcon, 
  DocumentTextIcon, 
  BuildingOfficeIcon,
  BanknotesIcon,
  ChartBarIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'

interface ChartOfAccount {
  id: string
  account_code: string
  account_name: string
  account_type: string
  parent_id?: string
  balance?: number
  total_debit?: number
  total_credit?: number
  is_active: boolean
}

interface LedgerEntry {
  id: string
  journal_entry_id: string
  account_id: string
  description: string
  debit: number
  credit: number
  running_balance: number
  entry_date: string
  entry_number?: string
  entry_description?: string
  source_module?: string
  source_transaction_id?: string
  created_at: string
  journal_entry?: {
    id: string
    entry_number: string
    entry_date: string
    description: string
    status: string
    source_module?: string
    source_transaction_id?: string
  }
}

interface JournalEntryLine {
  id: string
  account_id: string
  description?: string
  narration?: string
  debit_amount: number
  credit_amount: number
  sort_order: number
  chart_of_accounts?: {
    id: string
    account_code: string
    account_name: string
    account_type?: string
  }
}

interface JournalEntry {
  id: string
  entry_number: string
  entry_date: string
  description: string
  total_debit: number
  total_credit: number
  status: string
  reference_type?: string
  source_module?: string
  source_transaction_id?: string
  journal_entry_lines?: JournalEntryLine[]
}

interface Vendor {
  id: string
  name: string
  vendor_name?: string
  contact_person: string
  email: string
  phone: string
  address: string
  city: string
  state: string
  zip_code?: string
  postal_code?: string
  cr_number?: string
  tax_number?: string
  national_address?: string
}

interface Expense {
  id: string
  expense_date: string
  due_date?: string
  amount: number
  subtotal?: number
  tax_amount: number
  total_amount: number
  description: string
  category?: string
  category_id?: string
  vendor_id?: string
  vendor_name?: string
  payee_name?: string
  vat_number?: string
  payment_method: string
  payment_status?: string
  paid_amount?: number
  bill_discount?: number
  bill_discount_type?: string
  expense_number?: string
  expense_items?: ExpenseItem[]
}

interface ExpenseItem {
  id?: string
  item_name: string
  item_description?: string
  quantity: number
  unit_price: number
  tax_rate: number
  tax_amount: number
  item_discount: number
  item_discount_type: string
  subtotal: number
  total_amount: number
  link_to_inventory?: boolean
  inventory_id?: string
}

const Accounting: React.FC = () => {
  const { user } = useAuth()
  const isSuperAdmin = user?.roles?.some((r: { name: string }) => r.name === 'Super Admin') ?? false

  const [activeTab, setActiveTab] = useState('chart')
  const [chartOfAccounts, setChartOfAccounts] = useState<ChartOfAccount[]>([])
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([])
  const [vendors, setVendors] = useState<Vendor[]>([])
  const [expenses, setExpenses] = useState<Expense[]>([])
  const [expenseCategories, setExpenseCategories] = useState<any[]>([])
  const [inventoryItems, setInventoryItems] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  
  // Account detail modal states
  const [showAccountDetailModal, setShowAccountDetailModal] = useState(false)
  const [selectedAccount, setSelectedAccount] = useState<ChartOfAccount | null>(null)
  const [accountLedger, setAccountLedger] = useState<LedgerEntry[]>([])
  const [ledgerLoading, setLedgerLoading] = useState(false)
  const [ledgerFilters, setLedgerFilters] = useState({
    start_date: '',
    end_date: '',
    page: 1,
    limit: 100
  })
  const [ledgerSummary, setLedgerSummary] = useState<any>(null)
  const [ledgerPagination, setLedgerPagination] = useState<any>(null)

  // Modal states
  const [showModal, setShowModal] = useState(false)
  const [modalType, setModalType] = useState<'add' | 'edit'>('add')
  const [editingItem, setEditingItem] = useState<any>(null)
  const [showViewModal, setShowViewModal] = useState(false)
  const [viewingExpense, setViewingExpense] = useState<Expense | null>(null)
  const [showDeleteExpenseModal, setShowDeleteExpenseModal] = useState(false)
  const [expenseToDelete, setExpenseToDelete] = useState<Expense | null>(null)
  const [showDeleteVendorModal, setShowDeleteVendorModal] = useState(false)
  const [vendorToDelete, setVendorToDelete] = useState<Vendor | null>(null)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [expenseForPayment, setExpenseForPayment] = useState<Expense | null>(null)
  const [paymentForm, setPaymentForm] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    payment_amount: '',
    payment_method: 'cash',
    payment_reference: '',
    notes: ''
  })
  const [savingExpense, setSavingExpense] = useState(false)
  const [savingPayment, setSavingPayment] = useState(false)
  
  // Expense Category/Type management states
  const [showExpenseTypeModal, setShowExpenseTypeModal] = useState(false)
  const [editingExpenseType, setEditingExpenseType] = useState<any>(null)
  const [expenseTypeForm, setExpenseTypeForm] = useState({
    category_name: '',
    account_id: '',
    description: '',
    is_active: true
  })
  
  // Reports states
  const [showReportModal, setShowReportModal] = useState(false)
  const [reportType, setReportType] = useState<'trial-balance' | 'profit-loss' | 'balance-sheet' | 'payables' | 'receivables' | 'vat' | 'cash-flow' | 'general-ledger' | 'journal-entries' | 'sales-by-category' | 'expense-by-category' | null>(null)
  const [reportData, setReportData] = useState<any>(null)
  const [reportLoading, setReportLoading] = useState(false)
  const [reportFilters, setReportFilters] = useState({
    start_date: '',
    end_date: '',
    as_of_date: '',
    aging_days: '', // Number of days to add to as_of_date for aging calculations (for payables/receivables)
    aging_filter: '', // For payables/receivables: 'all', '0-30', '31-60', '61-90', '90+'
    account_id: '',
    status: '',
    source_module: '',
    entry_number: '',
    order_type: '',
    category_id: '',
    expense_type: '',
    page: '1',
    limit: '100'
  })
  
  // Report drill-down states - Modal approach
  const [showAccountDetailsModal, setShowAccountDetailsModal] = useState(false)
  const [selectedAccountForDetails, setSelectedAccountForDetails] = useState<any>(null)
  const [accountDetails, setAccountDetails] = useState<any>(null)
  const [loadingAccountDetails, setLoadingAccountDetails] = useState(false)
  
  // Transaction/Journal Entry Detail Modal
  const [showTransactionDetailModal, setShowTransactionDetailModal] = useState(false)
  const [selectedTransaction, setSelectedTransaction] = useState<LedgerEntry | null>(null)
  const [transactionDetail, setTransactionDetail] = useState<any>(null)
  const [loadingTransactionDetail, setLoadingTransactionDetail] = useState(false)
  
  // Source transaction view states
  const [showSourceModal, setShowSourceModal] = useState(false)
  const [sourceTransaction, setSourceTransaction] = useState<any>(null)
  const [sourceLoading, setSourceLoading] = useState(false)
  
  // Journal Entry Detail View states
  const [showJournalDetailModal, setShowJournalDetailModal] = useState(false)
  const [viewingJournalEntry, setViewingJournalEntry] = useState<JournalEntry | null>(null)
  const [journalDetailLoading, setJournalDetailLoading] = useState(false)

  // Filter states for each tab
  const [chartFilters, setChartFilters] = useState({
    search: '',
    account_type: '',
    is_active: ''
  })
  const [journalFilters, setJournalFilters] = useState({
    search: '',
    start_date: '',
    end_date: '',
    status: ''
  })
  const [vendorFilters, setVendorFilters] = useState({
    search: '',
    city: '',
    vendor_id: ''
  })
  const [expenseFilters, setExpenseFilters] = useState({
    search: '',
    category_id: '',
    vendor_id: '',
    payment_status: '',
    start_date: '',
    end_date: ''
  })
  const [expenseTypeFilters, setExpenseTypeFilters] = useState({
    search: '',
    is_active: ''
  })

  // Filtered data states
  const [filteredChartOfAccounts, setFilteredChartOfAccounts] = useState<ChartOfAccount[]>([])
  const [filteredJournalEntries, setFilteredJournalEntries] = useState<JournalEntry[]>([])
  const [filteredVendors, setFilteredVendors] = useState<Vendor[]>([])
  const [filteredExpenses, setFilteredExpenses] = useState<Expense[]>([])
  const [filteredExpenseCategories, setFilteredExpenseCategories] = useState<any[]>([])
  
  // Pagination states
  const [chartPagination, setChartPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 })
  const [journalPagination, setJournalPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 })
  const [vendorPagination, setVendorPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 })
  const [expensePagination, setExpensePagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 })
  const [expenseTypePagination, setExpenseTypePagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 })
  const [expenseAnalytics, setExpenseAnalytics] = useState<any>(null)

  // Form states
  const [chartForm, setChartForm] = useState({
    account_code: '',
    account_name: '',
    account_type: 'asset',
    parent_id: '',
    is_active: true
  })

  const [vendorForm, setVendorForm] = useState({
    name: '',
    vendor_name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    postal_code: '',
    cr_number: '',
    tax_number: '',
    national_address: ''
  })

  const [expenseForm, setExpenseForm] = useState({
    expense_date: new Date().toISOString().split('T')[0],
    due_date: '',
    description: '',
    category_id: '',
    vendor_id: '',
    vendor_name: '',
    vat_number: '',
    payment_method: 'none',
    payment_status: 'unpaid',
    paid_amount: '',
    bill_discount: '',
    bill_discount_type: 'amount'
  })

  const [expenseItems, setExpenseItems] = useState<ExpenseItem[]>([
    {
      item_name: '',
      item_description: '',
      quantity: 1,
      unit_price: 0,
      tax_rate: 15, // Default VAT rate for Saudi Arabia
      tax_amount: 0,
      item_discount: 0,
      item_discount_type: 'amount',
      subtotal: 0,
      total_amount: 0
    }
  ])

  const [vendorSearchTerm, setVendorSearchTerm] = useState('')
  const [showVendorDropdown, setShowVendorDropdown] = useState(false)
  const [filteredVendorsForExpense, setFilteredVendorsForExpense] = useState<Vendor[]>([])
  const [inventorySearchTerms, setInventorySearchTerms] = useState<{ [key: number]: string }>({})
  const [showInventoryDropdowns, setShowInventoryDropdowns] = useState<{ [key: number]: boolean }>({})
  const [filteredInventoryItems, setFilteredInventoryItems] = useState<{ [key: number]: any[] }>({})

  // Journal Entry form states
  const [journalForm, setJournalForm] = useState({
    entry_date: new Date().toISOString().split('T')[0],
    description: '',
    journal_lines: [{ account_id: '', description: '', debit_amount: '', credit_amount: '' }]
  })

  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')

  useEffect(() => {
    // Reset pagination when switching tabs
    setChartPagination({ page: 1, limit: 20, total: 0, total_pages: 0 })
    setJournalPagination({ page: 1, limit: 20, total: 0, total_pages: 0 })
    setVendorPagination({ page: 1, limit: 20, total: 0, total_pages: 0 })
    setExpensePagination({ page: 1, limit: 20, total: 0, total_pages: 0 })
    setExpenseTypePagination({ page: 1, limit: 20, total: 0, total_pages: 0 })
    loadData(1)
  }, [activeTab])

  // Apply filters when data or filters change
  useEffect(() => {
    applyFilters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartOfAccounts, journalEntries, vendors, expenses, expenseCategories, chartFilters, journalFilters, vendorFilters, expenseFilters, expenseTypeFilters, activeTab])

  const applyFilters = () => {
    switch (activeTab) {
      case 'chart':
        let filtered = [...chartOfAccounts]
        if (chartFilters.search) {
          const searchLower = chartFilters.search.toLowerCase()
          filtered = filtered.filter(acc => 
            acc.account_code.toLowerCase().includes(searchLower) ||
            acc.account_name.toLowerCase().includes(searchLower)
          )
        }
        if (chartFilters.account_type) {
          filtered = filtered.filter(acc => acc.account_type === chartFilters.account_type)
        }
        if (chartFilters.is_active !== '') {
          filtered = filtered.filter(acc => acc.is_active === (chartFilters.is_active === 'true'))
        }
        setFilteredChartOfAccounts(filtered)
        break
      case 'journal':
        let filteredJournal = [...journalEntries]
        if (journalFilters.search) {
          const searchLower = journalFilters.search.toLowerCase()
          filteredJournal = filteredJournal.filter(entry =>
            entry.entry_number.toLowerCase().includes(searchLower) ||
            entry.description.toLowerCase().includes(searchLower)
          )
        }
        if (journalFilters.start_date) {
          filteredJournal = filteredJournal.filter(entry => entry.entry_date >= journalFilters.start_date)
        }
        if (journalFilters.end_date) {
          filteredJournal = filteredJournal.filter(entry => entry.entry_date <= journalFilters.end_date)
        }
        if (journalFilters.status) {
          filteredJournal = filteredJournal.filter(entry => entry.status === journalFilters.status)
        }
        setFilteredJournalEntries(filteredJournal)
        break
      case 'vendors':
        let filteredVendors = [...vendors]
        if (vendorFilters.search) {
          const searchLower = vendorFilters.search.toLowerCase()
          filteredVendors = filteredVendors.filter(vendor =>
            (vendor.name || vendor.vendor_name || '').toLowerCase().includes(searchLower) ||
            (vendor.contact_person || '').toLowerCase().includes(searchLower) ||
            (vendor.email || '').toLowerCase().includes(searchLower) ||
            (vendor.phone || '').toLowerCase().includes(searchLower)
          )
        }
        if (vendorFilters.city) {
          filteredVendors = filteredVendors.filter(vendor => vendor.city === vendorFilters.city)
        }
        if (vendorFilters.vendor_id) {
          filteredVendors = filteredVendors.filter(vendor => vendor.id === vendorFilters.vendor_id)
        }
        setFilteredVendors(filteredVendors)
        break
      case 'expenses':
        let filteredExpenses = [...expenses]
        if (expenseFilters.search) {
          const searchLower = expenseFilters.search.toLowerCase()
          filteredExpenses = filteredExpenses.filter(expense =>
            (expense.expense_number || '').toLowerCase().includes(searchLower) ||
            (expense.description || '').toLowerCase().includes(searchLower) ||
            (expense.payee_name || '').toLowerCase().includes(searchLower)
          )
        }
        if (expenseFilters.category_id) {
          filteredExpenses = filteredExpenses.filter(expense => expense.category_id === expenseFilters.category_id)
        }
        if (expenseFilters.vendor_id) {
          filteredExpenses = filteredExpenses.filter(expense => expense.vendor_id === expenseFilters.vendor_id)
        }
        if (expenseFilters.payment_status) {
          filteredExpenses = filteredExpenses.filter(expense => expense.payment_status === expenseFilters.payment_status)
        }
        if (expenseFilters.start_date) {
          filteredExpenses = filteredExpenses.filter(expense => expense.expense_date >= expenseFilters.start_date)
        }
        if (expenseFilters.end_date) {
          filteredExpenses = filteredExpenses.filter(expense => expense.expense_date <= expenseFilters.end_date)
        }
        setFilteredExpenses(filteredExpenses)
        break
      case 'expense-types':
        let filteredTypes = [...expenseCategories]
        if (expenseTypeFilters.search) {
          const searchLower = expenseTypeFilters.search.toLowerCase()
          filteredTypes = filteredTypes.filter(type =>
            (type.category_name || '').toLowerCase().includes(searchLower) ||
            (type.description || '').toLowerCase().includes(searchLower)
          )
        }
        if (expenseTypeFilters.is_active !== '') {
          filteredTypes = filteredTypes.filter(type => type.is_active === (expenseTypeFilters.is_active === 'true'))
        }
        setFilteredExpenseCategories(filteredTypes)
        break
    }
  }

  const loadData = async (page: number = 1) => {
    setLoading(true)
    try {
      switch (activeTab) {
        case 'chart':
          const chartRes = await apiService.getChartOfAccounts({ page, limit: chartPagination.limit })
          if (chartRes.success && chartRes.data && Array.isArray(chartRes.data)) {
            setChartOfAccounts(chartRes.data as ChartOfAccount[])
            if (chartRes.pagination) {
              setChartPagination(chartRes.pagination)
            }
          }
          break
        case 'journal':
          const journalRes = await apiService.getJournalEntries({ page, limit: journalPagination.limit })
          if (journalRes.success && journalRes.data && Array.isArray(journalRes.data)) {
            setJournalEntries(journalRes.data as JournalEntry[])
            if (journalRes.pagination) {
              setJournalPagination(journalRes.pagination)
            }
          }
          break
        case 'vendors':
          const vendorsRes1 = await apiService.getVendors({ page, limit: vendorPagination.limit })
          if (vendorsRes1.success && vendorsRes1.data && Array.isArray(vendorsRes1.data)) {
            // Map vendor_name to name for compatibility
            const mappedVendors = (vendorsRes1.data as any[]).map((vendor: any) => ({
              ...vendor,
              name: vendor.name || vendor.vendor_name,
              zip_code: vendor.zip_code || vendor.postal_code
            }))
            setVendors(mappedVendors)
            if (vendorsRes1.pagination) {
              setVendorPagination(vendorsRes1.pagination)
            }
          }
          break
        case 'expenses':
          const expensesRes = await apiService.getExpenses({ page, limit: expensePagination.limit })
          if (expensesRes.success && expensesRes.data && Array.isArray(expensesRes.data)) {
            setExpenses(expensesRes.data as Expense[])
            if (expensesRes.pagination) {
              setExpensePagination(expensesRes.pagination)
            }
          }
          // Load expense analytics
          const analyticsRes = await apiService.getExpenseAnalytics()
          if (analyticsRes.success && analyticsRes.data) {
            setExpenseAnalytics(analyticsRes.data)
          }
          // Load expense categories for dropdown (no pagination needed for dropdown)
          const categoriesRes = await apiService.getExpenseCategories()
          if (categoriesRes.success && categoriesRes.data && Array.isArray(categoriesRes.data)) {
            setExpenseCategories(categoriesRes.data as any[])
          }
          // Load vendors for dropdown (no pagination needed for dropdown)
          const vendorsRes2 = await apiService.getVendors()
          if (vendorsRes2.success && vendorsRes2.data && Array.isArray(vendorsRes2.data)) {
            const mappedVendors = (vendorsRes2.data as any[]).map((vendor: any) => ({
              ...vendor,
              name: vendor.name || vendor.vendor_name,
              zip_code: vendor.zip_code || vendor.postal_code
            }))
            setVendors(mappedVendors)
          }
          // Load inventory items for linking
          const inventoryRes = await apiService.getInventory()
          if (inventoryRes.success && inventoryRes.data && Array.isArray(inventoryRes.data)) {
            setInventoryItems(inventoryRes.data)
          }
          break
        case 'expense-types':
          const expenseTypesRes = await apiService.getExpenseCategories({ page, limit: expenseTypePagination.limit })
          if (expenseTypesRes.success && expenseTypesRes.data && Array.isArray(expenseTypesRes.data)) {
            setExpenseCategories(expenseTypesRes.data as any[])
            if (expenseTypesRes.pagination) {
              setExpenseTypePagination(expenseTypesRes.pagination)
            }
          }
          break
      }
    } catch (error) {
      console.error('Error loading data:', error)
      showToastMessage('Failed to load data', 'error')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount: number | string | null | undefined) => {
    if (amount === null || amount === undefined) {
      return 'SAR 0.00'
    }
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
    if (isNaN(numAmount)) {
      return 'SAR 0.00'
    }
    return `SAR ${numAmount.toFixed(2)}`
  }

  const showToastMessage = (message: string, type: 'success' | 'error') => {
    setToastMessage(message)
    setToastType(type)
    setShowToast(true)
    setTimeout(() => setShowToast(false), 3000)
  }

  // Chart of Accounts handlers
  const handleAddChartAccount = () => {
    setModalType('add')
    setEditingItem(null)
    setChartForm({
      account_code: '',
      account_name: '',
      account_type: 'asset',
      parent_id: '',
      is_active: true
    })
    setShowModal(true)
  }

  const loadAccountLedger = async (accountId: string, filters?: any) => {
    setLedgerLoading(true)
    try {
      const filtersToUse = filters || ledgerFilters
      const response = await apiService.getAccountLedger(accountId, filtersToUse)
      if (response.success && response.data) {
        setAccountLedger((response.data as any).ledger || [])
        setLedgerSummary((response.data as any).summary || null)
        setLedgerPagination((response.data as any).pagination || null)
      }
    } catch (error) {
      console.error('Error loading account ledger:', error)
      showToastMessage('Failed to load account ledger', 'error')
    } finally {
      setLedgerLoading(false)
    }
  }

  const handleViewAccountDetail = async (account: ChartOfAccount) => {
    setSelectedAccount(account)
    setShowAccountDetailModal(true)
    await loadAccountLedger(account.id)
  }

  const handleExportLedger = (format: 'csv' | 'pdf') => {
    if (!selectedAccount || !accountLedger.length) {
      showToastMessage('No data to export', 'error')
      return
    }

    if (format === 'csv') {
      // Create CSV content
      const headers = ['Date', 'Entry Number', 'Description', 'Debit', 'Credit', 'Balance']
      const rows = accountLedger.map(entry => [
        entry.entry_date || '',
        entry.entry_number || '',
        entry.description || '',
        entry.debit.toFixed(2),
        entry.credit.toFixed(2),
        entry.running_balance.toFixed(2)
      ])
      
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n')
      
      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${selectedAccount.account_code}_${selectedAccount.account_name}_ledger.csv`
      a.click()
      window.URL.revokeObjectURL(url)
      showToastMessage('Ledger exported to CSV', 'success')
    } else {
      showToastMessage('PDF export coming soon', 'error')
    }
  }

  // Handle clicking on a reference in ledger description
  const handleViewSourceTransaction = async (entry: LedgerEntry) => {
    if (!entry.source_transaction_id || !entry.source_module) {
      return
    }
    
    setSourceLoading(true)
    setShowSourceModal(true)
    setSourceTransaction(null)
    
    try {
      if (entry.source_module === 'Accounting') {
        // Fetch expense details
        const response = await apiService.getExpense(entry.source_transaction_id)
        if (response.success && response.data) {
          setSourceTransaction({
            type: 'expense',
            data: response.data
          })
        } else {
          showToastMessage('Failed to load expense details', 'error')
        }
      } else if (entry.source_module === 'POS') {
        // Fetch order details
        const response = await apiService.getOrder(entry.source_transaction_id)
        if (response.success && response.data) {
          setSourceTransaction({
            type: 'order',
            data: response.data
          })
        } else {
          showToastMessage('Failed to load order details', 'error')
        }
      } else if (entry.source_module === 'Payroll') {
        // Fetch payroll details
        const response = await apiService.getPayroll(entry.source_transaction_id)
        if (response.success && response.data) {
          setSourceTransaction({
            type: 'payroll',
            data: response.data
          })
        } else {
          showToastMessage('Failed to load payroll details', 'error')
        }
      }
    } catch (error) {
      console.error('Error fetching source transaction:', error)
      showToastMessage('Failed to load transaction details', 'error')
    } finally {
      setSourceLoading(false)
    }
  }

  const handleEditChartAccount = (account: ChartOfAccount) => {
    setModalType('edit')
    setEditingItem(account)
    setChartForm({
      account_code: account.account_code,
      account_name: account.account_name,
      account_type: account.account_type,
      parent_id: account.parent_id || '',
      is_active: account.is_active
    })
    setShowModal(true)
  }

  const handleSaveChartAccount = async () => {
    try {
      if (!chartForm.account_code || !chartForm.account_name) {
        showToastMessage('Please fill in all required fields', 'error')
        return
      }

      const data = {
        ...chartForm,
        parent_id: chartForm.parent_id || null
      }

      let response
      if (modalType === 'add') {
        response = await apiService.createChartOfAccount(data)
      } else {
        response = await apiService.updateChartOfAccount(editingItem.id, data)
      }

      if (response.success) {
        showToastMessage(
          modalType === 'add' ? 'Account created successfully' : 'Account updated successfully',
          'success'
        )
        setShowModal(false)
        loadData()
      } else {
        showToastMessage(response.error || 'Failed to save account', 'error')
      }
    } catch (error) {
      console.error('Error saving account:', error)
      showToastMessage('An error occurred', 'error')
    }
  }

  // Vendor handlers
  const handleAddVendor = () => {
    setModalType('add')
    setEditingItem(null)
    setVendorForm({
      name: '',
      vendor_name: '',
      contact_person: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      postal_code: '',
      cr_number: '',
      tax_number: '',
      national_address: ''
    })
    setShowModal(true)
  }

  const handleEditVendor = (vendor: Vendor) => {
    setModalType('edit')
    setEditingItem(vendor)
    setVendorForm({
      name: vendor.name || vendor.vendor_name || '',
      vendor_name: vendor.vendor_name || vendor.name || '',
      contact_person: vendor.contact_person || '',
      email: vendor.email || '',
      phone: vendor.phone || '',
      address: vendor.address || '',
      city: vendor.city || '',
      state: vendor.state || '',
      zip_code: vendor.zip_code || vendor.postal_code || '',
      postal_code: vendor.postal_code || vendor.zip_code || '',
      cr_number: vendor.cr_number || '',
      tax_number: vendor.tax_number || '',
      national_address: vendor.national_address || ''
    })
    setShowModal(true)
  }

  const handleSaveVendor = async () => {
    try {
      if (!vendorForm.name && !vendorForm.vendor_name || !vendorForm.phone) {
        showToastMessage('Please fill in all required fields', 'error')
        return
      }

      // Prepare vendor data for backend
      const vendorData = {
        vendor_name: vendorForm.name || vendorForm.vendor_name,
        contact_person: vendorForm.contact_person || null,
        email: vendorForm.email,
        phone: vendorForm.phone || null,
        address: vendorForm.address || null,
        city: vendorForm.city || null,
        state: vendorForm.state || null,
        postal_code: vendorForm.postal_code || vendorForm.zip_code || null,
        cr_number: vendorForm.cr_number || null,
        tax_number: vendorForm.tax_number || null,
        national_address: vendorForm.national_address || null
      }

      let response
      if (modalType === 'add') {
        response = await apiService.createVendor(vendorData)
      } else {
        response = await apiService.updateVendor(editingItem.id, vendorData)
      }

      if (response.success) {
        showToastMessage(
          modalType === 'add' ? 'Vendor created successfully' : 'Vendor updated successfully',
          'success'
        )
        setShowModal(false)
        loadData()
      } else {
        showToastMessage(response.error || 'Failed to save vendor', 'error')
      }
    } catch (error) {
      console.error('Error saving vendor:', error)
      showToastMessage('An error occurred', 'error')
    }
  }

  const handleDeleteVendor = (vendor: Vendor) => {
    setVendorToDelete(vendor)
    setShowDeleteVendorModal(true)
  }

  const confirmDeleteVendor = async () => {
    if (!vendorToDelete) return

    try {
      const response = await apiService.deleteVendor(vendorToDelete.id)
      if (response.success) {
        showToastMessage('Vendor deleted successfully', 'success')
        loadData()
        setShowDeleteVendorModal(false)
        setVendorToDelete(null)
      } else {
        showToastMessage(response.error || 'Failed to delete vendor', 'error')
      }
    } catch (error) {
      console.error('Error deleting vendor:', error)
      showToastMessage('An error occurred', 'error')
    }
  }

  // Expense item calculation functions
  const calculateItemTotal = (item: ExpenseItem) => {
    const itemSubtotal = item.quantity * item.unit_price
    let discountAmount = 0
    if (item.item_discount_type === 'percentage') {
      discountAmount = (itemSubtotal * item.item_discount) / 100
    } else {
      discountAmount = item.item_discount
    }
    const subtotal = itemSubtotal - discountAmount
    const taxAmount = (subtotal * item.tax_rate) / 100
    const totalAmount = subtotal + taxAmount
    
    return {
      subtotal: Math.max(0, subtotal),
      tax_amount: Math.max(0, taxAmount),
      total_amount: Math.max(0, totalAmount)
    }
  }

  const calculateBillTotals = () => {
    const itemsSubtotal = expenseItems.reduce((sum, item) => {
      const calc = calculateItemTotal(item)
      return sum + calc.subtotal
    }, 0)

    let billDiscountAmount = 0
    if (expenseForm.bill_discount) {
      const discount = parseFloat(expenseForm.bill_discount) || 0
      if (expenseForm.bill_discount_type === 'percentage') {
        billDiscountAmount = (itemsSubtotal * discount) / 100
      } else {
        billDiscountAmount = discount
      }
    }

    const subtotalAfterDiscount = Math.max(0, itemsSubtotal - billDiscountAmount)
    
    const itemsTax = expenseItems.reduce((sum, item) => {
      const calc = calculateItemTotal(item)
      return sum + calc.tax_amount
    }, 0)

    const totalAmount = subtotalAfterDiscount + itemsTax

    return {
      subtotal: itemsSubtotal,
      bill_discount: billDiscountAmount,
      subtotal_after_discount: subtotalAfterDiscount,
      tax_amount: itemsTax,
      total_amount: totalAmount
    }
  }

  // Vendor search handler
  useEffect(() => {
    if (vendorSearchTerm) {
      const filtered = vendors.filter(vendor => 
        (vendor.name || vendor.vendor_name || '').toLowerCase().includes(vendorSearchTerm.toLowerCase()) ||
        (vendor.phone || '').includes(vendorSearchTerm) ||
        (vendor.email || '').toLowerCase().includes(vendorSearchTerm.toLowerCase())
      )
      setFilteredVendorsForExpense(filtered)
      setShowVendorDropdown(true)
    } else {
      setFilteredVendorsForExpense(vendors)
      setShowVendorDropdown(false)
    }
  }, [vendorSearchTerm, vendors])

  const handleVendorSelect = (vendor: Vendor) => {
    setExpenseForm({
      ...expenseForm,
      vendor_id: vendor.id,
      vendor_name: vendor.name || vendor.vendor_name || '',
      vat_number: vendor.tax_number || ''
    })
    setVendorSearchTerm(vendor.name || vendor.vendor_name || '')
    setShowVendorDropdown(false)
  }

  const handleAddExpenseItem = () => {
    setExpenseItems([...expenseItems, {
      item_name: '',
      item_description: '',
      quantity: 1,
      unit_price: 0,
      tax_rate: 15,
      tax_amount: 0,
      item_discount: 0,
      item_discount_type: 'amount',
      subtotal: 0,
      total_amount: 0,
      link_to_inventory: false,
      inventory_id: undefined
    }])
  }

  const handleRemoveExpenseItem = (index: number) => {
    if (expenseItems.length > 1) {
      setExpenseItems(expenseItems.filter((_, i) => i !== index))
      // Clean up search state for removed item
      const newSearchTerms = { ...inventorySearchTerms }
      const newFiltered = { ...filteredInventoryItems }
      const newDropdowns = { ...showInventoryDropdowns }
      delete newSearchTerms[index]
      delete newFiltered[index]
      delete newDropdowns[index]
      // Reindex remaining items
      const reindexedSearchTerms: { [key: number]: string } = {}
      const reindexedFiltered: { [key: number]: any[] } = {}
      const reindexedDropdowns: { [key: number]: boolean } = {}
      Object.keys(newSearchTerms).forEach((key) => {
        const oldIndex = parseInt(key)
        if (oldIndex > index) {
          reindexedSearchTerms[oldIndex - 1] = newSearchTerms[oldIndex]
          reindexedFiltered[oldIndex - 1] = newFiltered[oldIndex]
          reindexedDropdowns[oldIndex - 1] = newDropdowns[oldIndex]
        } else if (oldIndex < index) {
          reindexedSearchTerms[oldIndex] = newSearchTerms[oldIndex]
          reindexedFiltered[oldIndex] = newFiltered[oldIndex]
          reindexedDropdowns[oldIndex] = newDropdowns[oldIndex]
        }
      })
      setInventorySearchTerms(reindexedSearchTerms)
      setFilteredInventoryItems(reindexedFiltered)
      setShowInventoryDropdowns(reindexedDropdowns)
    }
  }

  const handleUpdateExpenseItem = (index: number, field: keyof ExpenseItem, value: any) => {
    const updatedItems = [...expenseItems]
    updatedItems[index] = { ...updatedItems[index], [field]: value }
    
    // Recalculate item totals
    const calc = calculateItemTotal(updatedItems[index])
    updatedItems[index] = {
      ...updatedItems[index],
      subtotal: calc.subtotal,
      tax_amount: calc.tax_amount,
      total_amount: calc.total_amount
    }
    
    setExpenseItems(updatedItems)
  }

  // Expense handlers
  const handleAddExpense = () => {
    setModalType('add')
    setEditingItem(null)
    setExpenseForm({
      expense_date: new Date().toISOString().split('T')[0],
      due_date: '',
      description: '',
      category_id: '',
      vendor_id: '',
      vendor_name: '',
      vat_number: '',
      payment_method: 'none',
      payment_status: 'unpaid',
      paid_amount: '',
      bill_discount: '',
      bill_discount_type: 'amount'
    })
    setExpenseItems([{
      item_name: '',
      item_description: '',
      quantity: 1,
      unit_price: 0,
      tax_rate: 15,
      tax_amount: 0,
      item_discount: 0,
      item_discount_type: 'amount',
      subtotal: 0,
      total_amount: 0
    }])
    setVendorSearchTerm('')
    setShowVendorDropdown(false)
    setShowModal(true)
  }

  const handleEditExpense = async (expense: Expense) => {
    setModalType('edit')
    setEditingItem(expense)
    
    // Load categories if not loaded yet (needed for display)
    if (expense.category_id && expenseCategories.length === 0) {
      const categoriesRes = await apiService.getExpenseCategories()
      if (categoriesRes.success && categoriesRes.data && Array.isArray(categoriesRes.data)) {
        setExpenseCategories(categoriesRes.data as any[])
      }
    }
    
    // Load expense items if available
    if (expense.expense_items && expense.expense_items.length > 0) {
      // Map expense items to the format expected by the form
      const mappedItems = expense.expense_items.map((item: any) => {
        // If unit_price is 0 but we have totals, calculate it
        let unitPrice = item.unit_price || 0
        if (unitPrice === 0 && item.total_amount > 0) {
          // Calculate unit price from total_amount, tax, and quantity
          const itemSubtotal = item.subtotal || (item.total_amount - item.tax_amount)
          unitPrice = itemSubtotal / (item.quantity || 1)
        }
        
        return {
          id: item.id,
          item_name: item.item_name || expense.description || '',
          item_description: item.item_description || '',
          quantity: item.quantity || 1,
          unit_price: unitPrice,
          tax_rate: item.tax_rate || 15,
          tax_amount: item.tax_amount || 0,
          item_discount: item.item_discount || 0,
          item_discount_type: item.item_discount_type || 'amount',
          subtotal: item.subtotal || 0,
          total_amount: item.total_amount || 0,
          link_to_inventory: item.link_to_inventory || false,
          inventory_id: item.inventory_id || undefined
        }
      })
      setExpenseItems(mappedItems)
    } else {
      // Fallback to old format - create a single item from expense totals
      // Calculate unit price from total_amount and tax
      const totalAmount = expense.total_amount || expense.amount || 0
      const taxAmount = expense.tax_amount || 0
      const subtotal = totalAmount - taxAmount
      const unitPrice = subtotal > 0 ? subtotal : totalAmount
      const taxRate = subtotal > 0 && taxAmount > 0 ? (taxAmount / subtotal) * 100 : 15
      
      setExpenseItems([{
        item_name: expense.description || 'Expense Item',
        item_description: '',
        quantity: 1,
        unit_price: unitPrice,
        tax_rate: taxRate,
        tax_amount: taxAmount,
        item_discount: 0,
        item_discount_type: 'amount',
        subtotal: subtotal,
        total_amount: totalAmount,
        link_to_inventory: false,
        inventory_id: undefined
      }])
    }
    
    // Load categories if not loaded yet
    if (expense.category_id && expenseCategories.length === 0) {
      const categoriesRes = await apiService.getExpenseCategories()
      if (categoriesRes.success && categoriesRes.data && Array.isArray(categoriesRes.data)) {
        setExpenseCategories(categoriesRes.data as any[])
      }
    }
    
    setExpenseForm({
      expense_date: expense.expense_date.split('T')[0],
      due_date: expense.due_date ? expense.due_date.split('T')[0] : '',
      description: expense.description || '',
      category_id: expense.category_id || '',
      vendor_id: expense.vendor_id || '',
      vendor_name: expense.vendor_name || '',
      vat_number: expense.vat_number || '',
      payment_method: expense.payment_method || (expense.payment_status === 'unpaid' ? 'none' : 'cash'),
      payment_status: expense.payment_status || 'unpaid',
      paid_amount: expense.paid_amount?.toString() || '0',
      bill_discount: expense.bill_discount?.toString() || '0',
      bill_discount_type: expense.bill_discount_type || 'amount'
    })
    
    // Set vendor search term
    if (expense.vendor_name) {
      setVendorSearchTerm(expense.vendor_name)
    }
    
    setShowModal(true)
  }

  const handleViewExpense = (expense: Expense) => {
    setViewingExpense(expense)
    setShowViewModal(true)
  }

  // Expense Type/Category Management Handlers
  const handleAddExpenseType = () => {
    setEditingExpenseType(null)
    setExpenseTypeForm({
      category_name: '',
      account_id: '',
      description: '',
      is_active: true
    })
    setShowExpenseTypeModal(true)
  }

  const handleEditExpenseType = (category: any) => {
    setEditingExpenseType(category)
    setExpenseTypeForm({
      category_name: category.category_name || '',
      account_id: category.account_id || '',
      description: category.description || '',
      is_active: category.is_active !== undefined ? category.is_active : true
    })
    setShowExpenseTypeModal(true)
  }

  const handleSaveExpenseType = async () => {
    try {
      if (!expenseTypeForm.category_name.trim()) {
        showToastMessage('Please enter a category name', 'error')
        return
      }

      if (editingExpenseType) {
        const response = await apiService.updateExpenseCategory(editingExpenseType.id, expenseTypeForm)
        if (response.success) {
          showToastMessage('Expense type updated successfully', 'success')
          await loadData()
          setShowExpenseTypeModal(false)
        } else {
          showToastMessage('Error updating expense type', 'error')
        }
      } else {
        const response = await apiService.createExpenseCategory(expenseTypeForm)
        if (response.success) {
          showToastMessage('Expense type created successfully', 'success')
          await loadData()
          setShowExpenseTypeModal(false)
        } else {
          showToastMessage('Error creating expense type', 'error')
        }
      }
    } catch (error) {
      console.error('Error saving expense type:', error)
      showToastMessage('Error saving expense type', 'error')
    }
  }

  const handleDeleteExpenseType = async (id: string) => {
    if (!confirm('Are you sure you want to delete this expense type?')) {
      return
    }

    try {
      const response = await apiService.deleteExpenseCategory(id)
      if (response.success) {
        showToastMessage('Expense type deleted successfully', 'success')
        await loadData()
      } else {
        showToastMessage('Error deleting expense type', 'error')
      }
    } catch (error) {
      console.error('Error deleting expense type:', error)
      showToastMessage('Error deleting expense type', 'error')
    }
  }

  // Report handlers
  const handleViewReport = async (type: 'trial-balance' | 'profit-loss' | 'balance-sheet' | 'payables' | 'receivables' | 'vat' | 'cash-flow' | 'general-ledger' | 'journal-entries' | 'sales-by-category' | 'expense-by-category') => {
    setReportType(type)
    setShowReportModal(true)
    setReportData(null)
    
    // Reset filters based on report type
    if (type === 'balance-sheet') {
      setReportFilters({
        start_date: '',
        end_date: '',
        as_of_date: new Date().toISOString().split('T')[0],
        aging_days: '',
        aging_filter: '',
        account_id: '',
        status: '',
        source_module: '',
        entry_number: '',
        order_type: '',
        category_id: '',
        expense_type: '',
        page: '1',
        limit: '100'
      })
    } else if (type === 'payables' || type === 'receivables') {
      const today = new Date().toISOString().split('T')[0]
      setReportFilters({
        start_date: '',
        end_date: '',
        as_of_date: today,
        aging_days: '0', // Default to 0 days (same as as_of_date)
        aging_filter: 'all',
        account_id: '',
        status: '',
        source_module: '',
        entry_number: '',
        order_type: '',
        category_id: '',
        expense_type: '',
        page: '1',
        limit: '100'
      })
    } else if (type === 'vat') {
      const today = new Date().toISOString().split('T')[0]
      const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]
      setReportFilters({
        start_date: firstDayOfMonth,
        end_date: today,
        as_of_date: '',
        aging_days: '',
        aging_filter: '',
        account_id: '',
        status: '',
        source_module: '',
        entry_number: '',
        order_type: '',
        category_id: '',
        expense_type: '',
        page: '1',
        limit: '100'
      })
    } else {
      setReportFilters({
        start_date: '',
        end_date: '',
        as_of_date: '',
        aging_days: '',
        aging_filter: '',
        account_id: '',
        status: '',
        source_module: '',
        entry_number: '',
        order_type: '',
        category_id: '',
        expense_type: '',
        page: '1',
        limit: '100'
      })
    }
  }

  const handleLoadReport = async () => {
    if (!reportType) return

    setReportLoading(true)
    try {
      let response
      if (reportType === 'trial-balance') {
        response = await apiService.getTrialBalance({
          start_date: reportFilters.start_date || undefined,
          end_date: reportFilters.end_date || undefined
        })
      } else if (reportType === 'profit-loss') {
        response = await apiService.getProfitLoss({
          start_date: reportFilters.start_date || undefined,
          end_date: reportFilters.end_date || undefined
        })
      } else if (reportType === 'balance-sheet') {
        response = await apiService.getBalanceSheet({
          as_of_date: reportFilters.as_of_date || undefined
        })
      } else if (reportType === 'payables') {
        response = await apiService.getPayables({
          as_of_date: reportFilters.as_of_date || undefined,
          aging_days: reportFilters.aging_days ? parseInt(reportFilters.aging_days) : 0,
          aging_filter: reportFilters.aging_filter || undefined
        })
      } else if (reportType === 'receivables') {
        response = await apiService.getReceivables({
          as_of_date: reportFilters.as_of_date || undefined,
          aging_days: reportFilters.aging_days ? parseInt(reportFilters.aging_days) : 0,
          aging_filter: reportFilters.aging_filter || undefined
        })
      } else if (reportType === 'vat') {
        response = await apiService.getVATReport({
          start_date: reportFilters.start_date || undefined,
          end_date: reportFilters.end_date || undefined
        })
      } else if (reportType === 'cash-flow') {
        response = await apiService.getCashFlow({
          start_date: reportFilters.start_date || undefined,
          end_date: reportFilters.end_date || undefined
        })
      } else if (reportType === 'general-ledger') {
        response = await apiService.getGeneralLedger({
          account_id: reportFilters.account_id || undefined,
          start_date: reportFilters.start_date || undefined,
          end_date: reportFilters.end_date || undefined,
          page: reportFilters.page ? parseInt(reportFilters.page) : 1,
          limit: reportFilters.limit ? parseInt(reportFilters.limit) : 100
        })
      } else if (reportType === 'journal-entries') {
        response = await apiService.getJournalEntryListing({
          start_date: reportFilters.start_date || undefined,
          end_date: reportFilters.end_date || undefined,
          status: reportFilters.status || undefined,
          source_module: reportFilters.source_module || undefined,
          entry_number: reportFilters.entry_number || undefined,
          page: reportFilters.page ? parseInt(reportFilters.page) : 1,
          limit: reportFilters.limit ? parseInt(reportFilters.limit) : 100
        })
      } else if (reportType === 'sales-by-category') {
        response = await apiService.getSalesByCategory({
          start_date: reportFilters.start_date || undefined,
          end_date: reportFilters.end_date || undefined,
          order_type: reportFilters.order_type || undefined
        })
      } else if (reportType === 'expense-by-category') {
        response = await apiService.getExpenseByCategory({
          start_date: reportFilters.start_date || undefined,
          end_date: reportFilters.end_date || undefined,
          category_id: reportFilters.category_id || undefined,
          expense_type: reportFilters.expense_type || undefined
        })
      }

      if (response && response.success) {
        setReportData(response.data)
      } else {
        showToastMessage('Error loading report', 'error')
      }
    } catch (error) {
      console.error('Error loading report:', error)
      showToastMessage('Error loading report', 'error')
    } finally {
      setReportLoading(false)
    }
  }

  const handleAccountClick = async (account: any) => {
    setSelectedAccountForDetails(account)
    setShowAccountDetailsModal(true)
    setAccountDetails(null)
    setLoadingAccountDetails(true)
    
    try {
      const filters: any = {}
      if (reportFilters.start_date) filters.start_date = reportFilters.start_date
      if (reportFilters.end_date) filters.end_date = reportFilters.end_date
      if (reportType === 'balance-sheet' && reportFilters.as_of_date) {
        filters.end_date = reportFilters.as_of_date
      }
      
      const response = await apiService.getAccountLedger(account.id, filters)
      
      if (response && response.success) {
        setAccountDetails(response.data)
      } else {
        showToastMessage('Error loading account details', 'error')
      }
    } catch (error) {
      console.error('Error loading account details:', error)
      showToastMessage('Error loading account details', 'error')
    } finally {
      setLoadingAccountDetails(false)
    }
  }

  const handleTransactionClick = async (entry: LedgerEntry) => {
    if (!entry.journal_entry?.id) {
      showToastMessage('Journal entry ID not found', 'error')
      return
    }
    
    setSelectedTransaction(entry)
    setShowTransactionDetailModal(true)
    setTransactionDetail(null)
    setLoadingTransactionDetail(true)
    
    try {
      const response = await apiService.getJournalEntryById(entry.journal_entry.id)
      
      if (response && response.success) {
        setTransactionDetail(response.data)
      } else {
        showToastMessage('Error loading transaction details', 'error')
      }
    } catch (error) {
      console.error('Error loading transaction details:', error)
      showToastMessage('Error loading transaction details', 'error')
    } finally {
      setLoadingTransactionDetail(false)
    }
  }

  // Excel Export Functions
  const exportTrialBalance = () => {
    if (!reportData) return
    
    const worksheetData: any[] = []
    
    // Add header
    worksheetData.push(['Trial Balance'])
    if (reportData.date_range) {
      const dateRange = []
      if (reportData.date_range.start_date) {
        dateRange.push(`From: ${new Date(reportData.date_range.start_date).toLocaleDateString()}`)
      }
      if (reportData.date_range.end_date) {
        dateRange.push(`To: ${new Date(reportData.date_range.end_date).toLocaleDateString()}`)
      }
      if (dateRange.length > 0) {
        worksheetData.push([dateRange.join(' - ')])
      }
    }
    worksheetData.push([]) // Empty row
    
    // Add column headers
    worksheetData.push(['Account Code', 'Account Name', 'Debit', 'Credit', 'Balance'])
    
    // Add account rows
    if (reportData.accounts && reportData.accounts.length > 0) {
      reportData.accounts.forEach((account: any) => {
        worksheetData.push([
          account.account_code,
          account.account_name,
          account.debit || 0,
          account.credit || 0,
          account.balance || 0
        ])
      })
    }
    
    // Add totals row
    if (reportData.totals) {
      worksheetData.push([]) // Empty row
      worksheetData.push([
        'Total',
        '',
        reportData.totals.total_debits || 0,
        reportData.totals.total_credits || 0,
        reportData.totals.difference || 0
      ])
    }
    
    // Create workbook and worksheet
    const ws = XLSX.utils.aoa_to_sheet(worksheetData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Trial Balance')
    
    // Generate filename with date
    const dateStr = reportData.date_range?.end_date 
      ? new Date(reportData.date_range.end_date).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0]
    const filename = `Trial_Balance_${dateStr}.xlsx`
    
    // Write file
    XLSX.writeFile(wb, filename)
  }

  const exportProfitLoss = () => {
    if (!reportData) return
    
    const worksheetData: any[] = []
    
    // Add header
    worksheetData.push(['Profit & Loss Statement'])
    if (reportData.date_range) {
      const dateRange = []
      if (reportData.date_range.start_date) {
        dateRange.push(`From: ${new Date(reportData.date_range.start_date).toLocaleDateString()}`)
      }
      if (reportData.date_range.end_date) {
        dateRange.push(`To: ${new Date(reportData.date_range.end_date).toLocaleDateString()}`)
      }
      if (dateRange.length > 0) {
        worksheetData.push([dateRange.join(' - ')])
      }
    }
    worksheetData.push([]) // Empty row
    
    // Revenue Section
    worksheetData.push(['REVENUE'])
    worksheetData.push(['Account', 'Amount'])
    
    if (reportData.revenues && reportData.revenues.length > 0) {
      reportData.revenues.forEach((revenue: any) => {
        worksheetData.push([
          `${revenue.account_code} - ${revenue.account_name}`,
          revenue.total || 0
        ])
      })
    }
    
    if (reportData.totals) {
      worksheetData.push(['Total Revenue', reportData.totals.total_revenue || 0])
    }
    
    worksheetData.push([]) // Empty row
    
    // Expenses Section
    worksheetData.push(['EXPENSES'])
    worksheetData.push(['Account', 'Amount'])
    
    if (reportData.expenses && reportData.expenses.length > 0) {
      reportData.expenses.forEach((expense: any) => {
        worksheetData.push([
          `${expense.account_code} - ${expense.account_name}`,
          expense.total || 0
        ])
      })
    }
    
    if (reportData.totals) {
      worksheetData.push(['Total Expenses', reportData.totals.total_expenses || 0])
    }
    
    worksheetData.push([]) // Empty row
    
    // Net Income
    if (reportData.totals) {
      worksheetData.push(['Net Income', reportData.totals.net_income || 0])
    }
    
    // Create workbook and worksheet
    const ws = XLSX.utils.aoa_to_sheet(worksheetData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Profit & Loss')
    
    // Generate filename with date
    const dateStr = reportData.date_range?.end_date 
      ? new Date(reportData.date_range.end_date).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0]
    const filename = `Profit_Loss_${dateStr}.xlsx`
    
    // Write file
    XLSX.writeFile(wb, filename)
  }

  const exportBalanceSheet = () => {
    if (!reportData) return
    
    const worksheetData: any[] = []
    
    // Add header
    worksheetData.push(['Balance Sheet'])
    if (reportData.as_of_date) {
      worksheetData.push([`As of: ${new Date(reportData.as_of_date).toLocaleDateString()}`])
    }
    worksheetData.push([]) // Empty row
    
    // Assets Section
    worksheetData.push(['ASSETS'])
    worksheetData.push(['Account', 'Balance'])
    
    if (reportData.assets && reportData.assets.length > 0) {
      reportData.assets.forEach((asset: any) => {
        worksheetData.push([
          `${asset.account_code} - ${asset.account_name}`,
          asset.balance || 0
        ])
      })
    }
    
    if (reportData.totals) {
      worksheetData.push(['Total Assets', reportData.totals.total_assets || 0])
    }
    
    worksheetData.push([]) // Empty row
    
    // Liabilities Section
    worksheetData.push(['LIABILITIES'])
    worksheetData.push(['Account', 'Balance'])
    
    if (reportData.liabilities && reportData.liabilities.length > 0) {
      reportData.liabilities.forEach((liability: any) => {
        worksheetData.push([
          `${liability.account_code} - ${liability.account_name}`,
          liability.balance || 0
        ])
      })
    }
    
    if (reportData.totals) {
      worksheetData.push(['Total Liabilities', reportData.totals.total_liabilities || 0])
    }
    
    worksheetData.push([]) // Empty row
    
    // Equity Section
    worksheetData.push(['EQUITY'])
    worksheetData.push(['Account', 'Balance'])
    
    if (reportData.equity && reportData.equity.length > 0) {
      reportData.equity.forEach((equityItem: any) => {
        worksheetData.push([
          `${equityItem.account_code} - ${equityItem.account_name}`,
          equityItem.balance || 0
        ])
      })
    }
    
    if (reportData.totals) {
      worksheetData.push(['Total Equity', reportData.totals.total_equity || 0])
    }
    
    worksheetData.push([]) // Empty row
    
    // Totals
    if (reportData.totals) {
      worksheetData.push(['Total Liabilities & Equity', (reportData.totals.total_liabilities || 0) + (reportData.totals.total_equity || 0)])
    }
    
    // Create workbook and worksheet
    const ws = XLSX.utils.aoa_to_sheet(worksheetData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Balance Sheet')
    
    // Generate filename with date
    const dateStr = reportData.as_of_date 
      ? new Date(reportData.as_of_date).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0]
    const filename = `Balance_Sheet_${dateStr}.xlsx`
    
    // Write file
    XLSX.writeFile(wb, filename)
  }

  // Report rendering functions
  const renderTrialBalance = () => {
    if (!reportData) return null
    
    return (
      <div>
        <div className="mb-4">
          <h3 className="text-lg font-semibold">Trial Balance</h3>
          {reportData.date_range && (reportData.date_range.start_date || reportData.date_range.end_date) && (
            <p className="text-sm text-gray-500">
              {reportData.date_range.start_date && `From: ${new Date(reportData.date_range.start_date).toLocaleDateString()}`}
              {reportData.date_range.start_date && reportData.date_range.end_date && ' - '}
              {reportData.date_range.end_date && `To: ${new Date(reportData.date_range.end_date).toLocaleDateString()}`}
            </p>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account Name</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Debit</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Credit</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
              </tr>
            </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {reportData.accounts && reportData.accounts.length > 0 ? (
                <>
                  {/* Current Assets */}
                  {reportData.current_asset_accounts && reportData.current_asset_accounts.length > 0 && (
                    <>
                      <tr className="bg-gray-50 dark:bg-gray-700">
                        <td colSpan={5} className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                          Current Assets
                        </td>
                      </tr>
                      {reportData.current_asset_accounts.map((account: any) => (
                        <tr 
                          key={account.id}
                          className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleAccountClick(account)}
                        >
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white pl-8">{account.account_code}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{account.account_name}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(account.debit)}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(account.credit)}</td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">{formatCurrency(account.balance)}</td>
                        </tr>
                      ))}
                    </>
                  )}
                  
                  {/* Non-Current Assets */}
                  {reportData.non_current_asset_accounts && reportData.non_current_asset_accounts.length > 0 && (
                    <>
                      <tr className="bg-gray-50 dark:bg-gray-700">
                        <td colSpan={5} className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                          Non-Current Assets
                        </td>
                      </tr>
                      {reportData.non_current_asset_accounts.map((account: any) => (
                        <tr 
                          key={account.id}
                          className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleAccountClick(account)}
                        >
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white pl-8">{account.account_code}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{account.account_name}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(account.debit)}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(account.credit)}</td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">{formatCurrency(account.balance)}</td>
                        </tr>
                      ))}
                    </>
                  )}
                  
                  {/* Current Liabilities */}
                  {reportData.current_liability_accounts && reportData.current_liability_accounts.length > 0 && (
                    <>
                      <tr className="bg-gray-50 dark:bg-gray-700">
                        <td colSpan={5} className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                          Current Liabilities
                        </td>
                      </tr>
                      {reportData.current_liability_accounts.map((account: any) => (
                        <tr 
                          key={account.id}
                          className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleAccountClick(account)}
                        >
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white pl-8">{account.account_code}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{account.account_name}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(account.debit)}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(account.credit)}</td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">{formatCurrency(account.balance)}</td>
                        </tr>
                      ))}
                    </>
                  )}
                  
                  {/* Non-Current Liabilities */}
                  {reportData.non_current_liability_accounts && reportData.non_current_liability_accounts.length > 0 && (
                    <>
                      <tr className="bg-gray-50 dark:bg-gray-700">
                        <td colSpan={5} className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                          Non-Current Liabilities
                        </td>
                      </tr>
                      {reportData.non_current_liability_accounts.map((account: any) => (
                        <tr 
                          key={account.id}
                          className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleAccountClick(account)}
                        >
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white pl-8">{account.account_code}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{account.account_name}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(account.debit)}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(account.credit)}</td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">{formatCurrency(account.balance)}</td>
                        </tr>
                      ))}
                    </>
                  )}
                  
                  {/* Other accounts (Revenue, Equity, etc.) */}
                  {reportData.accounts
                    .filter((account: any) => 
                      account.account_type?.toLowerCase() !== 'expense' && 
                      account.account_type?.toLowerCase() !== 'asset' && 
                      account.account_type?.toLowerCase() !== 'liability'
                    )
                    .map((account: any) => (
                      <tr 
                        key={account.id}
                        className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => handleAccountClick(account)}
                      >
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{account.account_code}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{account.account_name}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(account.debit)}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(account.credit)}</td>
                        <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">{formatCurrency(account.balance)}</td>
                      </tr>
                    ))}
                  
                  {/* Direct Expenses (Purchase) */}
                  {reportData.direct_expense_accounts && reportData.direct_expense_accounts.length > 0 && (
                    <>
                      <tr className="bg-gray-50 dark:bg-gray-700">
                        <td colSpan={5} className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                          Direct Expenses (Purchase)
                        </td>
                      </tr>
                      {reportData.direct_expense_accounts.map((account: any) => (
                        <tr 
                          key={account.id}
                          className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleAccountClick(account)}
                        >
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white pl-8">{account.account_code}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{account.account_name}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(account.debit)}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(account.credit)}</td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">{formatCurrency(account.balance)}</td>
                        </tr>
                      ))}
                    </>
                  )}
                  
                  {/* Indirect Expenses */}
                  {reportData.indirect_expense_accounts && reportData.indirect_expense_accounts.length > 0 && (
                    <>
                      <tr className="bg-gray-50 dark:bg-gray-700">
                        <td colSpan={5} className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                          Indirect Expenses (Rent, Maintenance, Repair, Utility, etc.)
                        </td>
                      </tr>
                      {reportData.indirect_expense_accounts.map((account: any) => (
                        <tr 
                          key={account.id}
                          className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleAccountClick(account)}
                        >
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white pl-8">{account.account_code}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{account.account_name}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(account.debit)}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(account.credit)}</td>
                          <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">{formatCurrency(account.balance)}</td>
                        </tr>
                      ))}
                    </>
                  )}
                </>
              ) : (
                <tr>
                  <td colSpan={5} className="px-4 py-4 text-center text-sm text-gray-500">No data available</td>
                </tr>
              )}
              {reportData.totals && (
                <tr className="bg-gray-100 dark:bg-gray-600 font-bold cursor-default border-t-2 border-gray-300 dark:border-gray-400">
                  <td colSpan={2} className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">Total</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">{formatCurrency(reportData.totals.total_debits)}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">{formatCurrency(reportData.totals.total_credits)}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">{formatCurrency(reportData.totals.difference)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    )
  }


  const renderProfitLoss = () => {
    if (!reportData) return null
    
    return (
      <div>
        <div className="mb-4">
          <h3 className="text-lg font-semibold">Profit & Loss Statement</h3>
          {reportData.date_range && (reportData.date_range.start_date || reportData.date_range.end_date) && (
            <p className="text-sm text-gray-500">
              {reportData.date_range.start_date && `From: ${new Date(reportData.date_range.start_date).toLocaleDateString()}`}
              {reportData.date_range.start_date && reportData.date_range.end_date && ' - '}
              {reportData.date_range.end_date && `To: ${new Date(reportData.date_range.end_date).toLocaleDateString()}`}
            </p>
          )}
        </div>
        
        {/* Revenue Section */}
        <div className="mb-6">
          <h4 className="text-md font-semibold mb-3">Revenue</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {reportData.revenues && reportData.revenues.length > 0 ? (
                  reportData.revenues.map((revenue: any) => (
                    <tr 
                      key={revenue.id}
                      className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => handleAccountClick(revenue)}
                    >
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{revenue.account_code} - {revenue.account_name}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(revenue.total)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={2} className="px-4 py-4 text-center text-sm text-gray-500">No revenue data</td>
                  </tr>
                )}
                {reportData.totals && (
                  <tr className="bg-gray-100 dark:bg-gray-600 font-bold cursor-default">
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">Total Revenue</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">{formatCurrency(reportData.totals.total_revenue)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Expenses Section */}
        <div className="mb-6">
          <h4 className="text-md font-semibold mb-3">Expenses</h4>
          
          {/* Direct Expenses (Purchase) */}
          <div className="mb-4">
            <h5 className="text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">Direct Expenses (Purchase)</h5>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {reportData.direct_expenses && reportData.direct_expenses.length > 0 ? (
                    reportData.direct_expenses.map((expense: any) => (
                      <tr 
                        key={expense.id}
                        className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => handleAccountClick(expense)}
                      >
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white pl-6">{expense.account_code} - {expense.account_name}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(expense.total)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={2} className="px-4 py-4 text-center text-sm text-gray-500 pl-6">No direct expense data</td>
                    </tr>
                  )}
                  {reportData.totals && (
                    <tr className="bg-gray-100 dark:bg-gray-600 font-bold cursor-default border-t-2 border-gray-300 dark:border-gray-400">
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 pl-6">Total Direct Expenses</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">{formatCurrency(reportData.totals.total_direct_expenses || 0)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Indirect Expenses */}
          <div className="mb-4">
            <h5 className="text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">Indirect Expenses (Rent, Maintenance, Repair, Utility, etc.)</h5>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {reportData.indirect_expenses && reportData.indirect_expenses.length > 0 ? (
                    reportData.indirect_expenses.map((expense: any) => (
                      <tr 
                        key={expense.id}
                        className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => handleAccountClick(expense)}
                      >
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white pl-6">{expense.account_code} - {expense.account_name}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(expense.total)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={2} className="px-4 py-4 text-center text-sm text-gray-500 pl-6">No indirect expense data</td>
                    </tr>
                  )}
                  {reportData.totals && (
                    <tr className="bg-gray-100 dark:bg-gray-600 font-bold cursor-default border-t-2 border-gray-300 dark:border-gray-400">
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 pl-6">Total Indirect Expenses</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">{formatCurrency(reportData.totals.total_indirect_expenses || 0)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Total Expenses Summary */}
          {reportData.totals && (
            <div className="mt-4">
              <div className="bg-gray-100 dark:bg-gray-600 font-bold cursor-default border-t-2 border-gray-300 dark:border-gray-400">
                <table className="min-w-full">
                  <tbody>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">Total Expenses</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">{formatCurrency(reportData.totals.total_expenses)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Net Income */}
        {reportData.totals && (
          <div className="mt-6 p-4 bg-primary-50 dark:bg-primary-900 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="text-lg font-bold">Net Income</span>
              <span className={`text-lg font-bold ${reportData.totals.net_income >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(reportData.totals.net_income)}
              </span>
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderPayables = () => {
    if (!reportData || !reportData.payables) return null
    
    const { payables, totals, as_of_date, aging_date } = reportData
    
    return (
      <div>
        <div className="mb-4">
          <h3 className="text-lg font-semibold">Accounts Payable</h3>
          {as_of_date && (
            <p className="text-sm text-gray-500">
              As of: {new Date(as_of_date).toLocaleDateString()}
              {aging_date && aging_date !== as_of_date && (
                <span className="ml-2">| Aging calculated as of: {new Date(aging_date).toLocaleDateString()}</span>
              )}
            </p>
          )}
        </div>
        
        {/* Aging Summary */}
        {totals && (
          <div className="mb-6 grid grid-cols-6 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">Current</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{formatCurrency(totals.current || 0)}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">0-30 Days</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{formatCurrency(totals['0-30'] || 0)}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">31-60 Days</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{formatCurrency(totals['31-60'] || 0)}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">61-90 Days</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{formatCurrency(totals['61-90'] || 0)}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">90+ Days</p>
              <p className="text-lg font-semibold text-red-600 dark:text-red-400">{formatCurrency(totals['90+'] || 0)}</p>
            </div>
            <div className="bg-primary-50 dark:bg-primary-900 p-4 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">Total</p>
              <p className="text-lg font-semibold text-primary-600 dark:text-primary-400">{formatCurrency(totals.total || 0)}</p>
            </div>
          </div>
        )}
        
        {/* Payables Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Amount</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Paid Amount</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Outstanding</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Aging Days</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Aging Bucket</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {payables && payables.length > 0 ? (
                payables.map((payable: any) => (
                  <tr key={payable.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{payable.reference_number}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{payable.vendor_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{new Date(payable.invoice_date).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{new Date(payable.due_date).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(payable.total_amount)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(payable.paid_amount)}</td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">{formatCurrency(payable.outstanding_amount)}</td>
                    <td className="px-4 py-3 text-sm text-center text-gray-900 dark:text-white">{payable.aging_days}</td>
                    <td className="px-4 py-3 text-sm text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        payable.aging_bucket === 'current' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        payable.aging_bucket === '0-30' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                        payable.aging_bucket === '31-60' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                        payable.aging_bucket === '61-90' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                        'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {payable.aging_bucket === 'current' ? 'Current' : payable.aging_bucket}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="px-4 py-4 text-center text-sm text-gray-500">No payables found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  const renderReceivables = () => {
    if (!reportData || !reportData.receivables) return null
    
    const { receivables, totals, as_of_date, aging_date } = reportData
    
    return (
      <div>
        <div className="mb-4">
          <h3 className="text-lg font-semibold">Accounts Receivable</h3>
          {as_of_date && (
            <p className="text-sm text-gray-500">
              As of: {new Date(as_of_date).toLocaleDateString()}
              {aging_date && aging_date !== as_of_date && (
                <span className="ml-2">| Aging calculated as of: {new Date(aging_date).toLocaleDateString()}</span>
              )}
            </p>
          )}
        </div>
        
        {/* Aging Summary */}
        {totals && (
          <div className="mb-6 grid grid-cols-6 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">Current</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{formatCurrency(totals.current || 0)}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">0-30 Days</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{formatCurrency(totals['0-30'] || 0)}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">31-60 Days</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{formatCurrency(totals['31-60'] || 0)}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">61-90 Days</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{formatCurrency(totals['61-90'] || 0)}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">90+ Days</p>
              <p className="text-lg font-semibold text-red-600 dark:text-red-400">{formatCurrency(totals['90+'] || 0)}</p>
            </div>
            <div className="bg-primary-50 dark:bg-primary-900 p-4 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">Total</p>
              <p className="text-lg font-semibold text-primary-600 dark:text-primary-400">{formatCurrency(totals.total || 0)}</p>
            </div>
          </div>
        )}
        
        {/* Receivables Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Amount</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Paid Amount</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Outstanding</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Aging Days</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Aging Bucket</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {receivables && receivables.length > 0 ? (
                receivables.map((receivable: any) => (
                  <tr key={receivable.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{receivable.reference_number}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{receivable.customer_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{new Date(receivable.invoice_date).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{new Date(receivable.due_date).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(receivable.total_amount)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(receivable.paid_amount)}</td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">{formatCurrency(receivable.outstanding_amount)}</td>
                    <td className="px-4 py-3 text-sm text-center text-gray-900 dark:text-white">{receivable.aging_days}</td>
                    <td className="px-4 py-3 text-sm text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        receivable.aging_bucket === 'current' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        receivable.aging_bucket === '0-30' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                        receivable.aging_bucket === '31-60' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                        receivable.aging_bucket === '61-90' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                        'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {receivable.aging_bucket === 'current' ? 'Current' : receivable.aging_bucket}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="px-4 py-4 text-center text-sm text-gray-500">No receivables found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  const renderVATReport = () => {
    if (!reportData) return null
    
    const { vat_output_details, vat_input_details, vat_control_details, totals, date_range } = reportData
    
    return (
      <div>
        <div className="mb-4">
          <h3 className="text-lg font-semibold">VAT Report</h3>
          {date_range && (date_range.start_date || date_range.end_date) && (
            <p className="text-sm text-gray-500">
              {date_range.start_date && `From: ${new Date(date_range.start_date).toLocaleDateString()}`}
              {date_range.start_date && date_range.end_date && ' - '}
              {date_range.end_date && `To: ${new Date(date_range.end_date).toLocaleDateString()}`}
            </p>
          )}
        </div>
        
        {/* VAT Summary */}
        {totals && (
          <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 dark:bg-blue-900 p-4 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">VAT Output</p>
              <p className="text-lg font-semibold text-blue-600 dark:text-blue-400">{formatCurrency(totals.vat_output || 0)}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">From Sales</p>
            </div>
            <div className="bg-green-50 dark:bg-green-900 p-4 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">VAT Input</p>
              <p className="text-lg font-semibold text-green-600 dark:text-green-400">{formatCurrency(totals.vat_input || 0)}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">From Expenses</p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900 p-4 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">VAT Control Balance</p>
              <p className="text-lg font-semibold text-purple-600 dark:text-purple-400">{formatCurrency(totals.vat_control || 0)}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Net VAT</p>
            </div>
            <div className={`p-4 rounded-lg ${(totals.net_vat || 0) >= 0 ? 'bg-red-50 dark:bg-red-900' : 'bg-green-50 dark:bg-green-900'}`}>
              <p className="text-xs text-gray-500 dark:text-gray-400">Net VAT Payable</p>
              <p className={`text-lg font-semibold ${(totals.net_vat || 0) >= 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                {formatCurrency(totals.net_vat || 0)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {(totals.net_vat || 0) >= 0 ? 'Amount to Pay' : 'Amount Recoverable'}
              </p>
            </div>
          </div>
        )}
        
        {/* VAT Output Details */}
        <div className="mb-6">
          <h4 className="text-md font-semibold mb-3">VAT Output (From Sales) - Account 2012</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entry Number</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">VAT Amount</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {vat_output_details && vat_output_details.length > 0 ? (
                  vat_output_details.map((entry: any, index: number) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {new Date(entry.entry_date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer font-medium">
                          {entry.entry_number}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{entry.description}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {entry.source_module === 'POS' ? `Order #${entry.source_transaction_id}` : entry.source_module || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">
                        {formatCurrency(entry.vat_amount)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-4 py-4 text-center text-sm text-gray-500">No VAT output transactions</td>
                  </tr>
                )}
                {totals && (
                  <tr className="bg-gray-100 dark:bg-gray-600 font-bold cursor-default border-t-2 border-gray-300 dark:border-gray-400">
                    <td colSpan={4} className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">Total VAT Output</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">{formatCurrency(totals.vat_output || 0)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* VAT Input Details */}
        <div className="mb-6">
          <h4 className="text-md font-semibold mb-3">VAT Input (From Expenses) - Account 1040</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entry Number</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">VAT Amount</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {vat_input_details && vat_input_details.length > 0 ? (
                  vat_input_details.map((entry: any, index: number) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {new Date(entry.entry_date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer font-medium">
                          {entry.entry_number}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{entry.description}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {entry.source_module === 'Accounting' ? `Expense #${entry.source_transaction_id}` : entry.source_module || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">
                        {formatCurrency(entry.vat_amount)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-4 py-4 text-center text-sm text-gray-500">No VAT input transactions</td>
                  </tr>
                )}
                {totals && (
                  <tr className="bg-gray-100 dark:bg-gray-600 font-bold cursor-default border-t-2 border-gray-300 dark:border-gray-400">
                    <td colSpan={4} className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">Total VAT Input</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">{formatCurrency(totals.vat_input || 0)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* VAT Control Details */}
        <div className="mb-6">
          <h4 className="text-md font-semibold mb-3">VAT Control Movements - Account 7000</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entry Number</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Debit</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Credit</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {vat_control_details && vat_control_details.length > 0 ? (
                  vat_control_details.map((entry: any, index: number) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {new Date(entry.entry_date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer font-medium">
                          {entry.entry_number}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{entry.description}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">
                        {entry.debit_amount > 0 ? formatCurrency(entry.debit_amount) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">
                        {entry.credit_amount > 0 ? formatCurrency(entry.credit_amount) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">
                        {formatCurrency(entry.running_balance || 0)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-4 py-4 text-center text-sm text-gray-500">No VAT control transactions</td>
                  </tr>
                )}
                {totals && (
                  <tr className="bg-gray-100 dark:bg-gray-600 font-bold cursor-default border-t-2 border-gray-300 dark:border-gray-400">
                    <td colSpan={3} className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">Total VAT Control Balance</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">{formatCurrency(totals.vat_control_debit || 0)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">{formatCurrency(totals.vat_control_credit || 0)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">{formatCurrency(totals.vat_control || 0)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  }

  const renderCashFlow = () => {
    if (!reportData) return null
    
    const { opening_balance, operating_activities, investing_activities, financing_activities, net_cash_flow, closing_balance, date_range } = reportData
    
    return (
      <div>
        <div className="mb-4">
          <h3 className="text-lg font-semibold">Cash Flow Statement</h3>
          {date_range && (date_range.start_date || date_range.end_date) && (
            <p className="text-sm text-gray-500">
              {date_range.start_date && `From: ${new Date(date_range.start_date).toLocaleDateString()}`}
              {date_range.start_date && date_range.end_date && ' - '}
              {date_range.end_date && `To: ${new Date(date_range.end_date).toLocaleDateString()}`}
            </p>
          )}
        </div>
        
        {/* Summary Cards */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 dark:bg-blue-900 p-4 rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Opening Balance</p>
            <p className="text-lg font-semibold text-blue-600 dark:text-blue-400">{formatCurrency(opening_balance || 0)}</p>
          </div>
          <div className="bg-green-50 dark:bg-green-900 p-4 rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Operating Activities</p>
            <p className="text-lg font-semibold text-green-600 dark:text-green-400">{formatCurrency(operating_activities?.total || 0)}</p>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900 p-4 rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Investing Activities</p>
            <p className="text-lg font-semibold text-purple-600 dark:text-purple-400">{formatCurrency(investing_activities?.total || 0)}</p>
          </div>
          <div className="bg-orange-50 dark:bg-orange-900 p-4 rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Financing Activities</p>
            <p className="text-lg font-semibold text-orange-600 dark:text-orange-400">{formatCurrency(financing_activities?.total || 0)}</p>
          </div>
        </div>
        
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Net Cash Flow</p>
            <p className={`text-lg font-semibold ${(net_cash_flow || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {formatCurrency(net_cash_flow || 0)}
            </p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Closing Balance</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">{formatCurrency(closing_balance || 0)}</p>
          </div>
        </div>
        
        {/* Operating Activities */}
        <div className="mb-6">
          <h4 className="text-md font-semibold mb-3">Operating Activities</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entry #</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {operating_activities?.transactions && operating_activities.transactions.length > 0 ? (
                  operating_activities.transactions.map((entry: any, index: number) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {new Date(entry.entry_date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer font-medium">
                          {entry.entry_number}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{entry.description}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{entry.account_code} - {entry.account_name}</td>
                      <td className={`px-4 py-3 text-sm text-right font-medium ${entry.amount >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {formatCurrency(entry.amount)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-4 py-4 text-center text-sm text-gray-500">No operating activities</td>
                  </tr>
                )}
                {operating_activities && (
                  <tr className="bg-gray-100 dark:bg-gray-600 font-bold cursor-default border-t-2">
                    <td colSpan={4} className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">Total Operating Activities</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">{formatCurrency(operating_activities.total || 0)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Investing Activities */}
        <div className="mb-6">
          <h4 className="text-md font-semibold mb-3">Investing Activities</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entry #</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {investing_activities?.transactions && investing_activities.transactions.length > 0 ? (
                  investing_activities.transactions.map((entry: any, index: number) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {new Date(entry.entry_date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer font-medium">
                          {entry.entry_number}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{entry.description}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{entry.account_code} - {entry.account_name}</td>
                      <td className={`px-4 py-3 text-sm text-right font-medium ${entry.amount >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {formatCurrency(entry.amount)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-4 py-4 text-center text-sm text-gray-500">No investing activities</td>
                  </tr>
                )}
                {investing_activities && (
                  <tr className="bg-gray-100 dark:bg-gray-600 font-bold cursor-default border-t-2">
                    <td colSpan={4} className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">Total Investing Activities</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">{formatCurrency(investing_activities.total || 0)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Financing Activities */}
        <div className="mb-6">
          <h4 className="text-md font-semibold mb-3">Financing Activities</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entry #</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {financing_activities?.transactions && financing_activities.transactions.length > 0 ? (
                  financing_activities.transactions.map((entry: any, index: number) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                        {new Date(entry.entry_date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer font-medium">
                          {entry.entry_number}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{entry.description}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{entry.account_code} - {entry.account_name}</td>
                      <td className={`px-4 py-3 text-sm text-right font-medium ${entry.amount >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {formatCurrency(entry.amount)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-4 py-4 text-center text-sm text-gray-500">No financing activities</td>
                  </tr>
                )}
                {financing_activities && (
                  <tr className="bg-gray-100 dark:bg-gray-600 font-bold cursor-default border-t-2">
                    <td colSpan={4} className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">Total Financing Activities</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">{formatCurrency(financing_activities.total || 0)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  }

  const renderGeneralLedger = () => {
    if (!reportData) return null
    
    const { entries, pagination, filters } = reportData
    
    return (
      <div>
        <div className="mb-4">
          <h3 className="text-lg font-semibold">General Ledger</h3>
          {filters && (filters.start_date || filters.end_date) && (
            <p className="text-sm text-gray-500">
              {filters.start_date && `From: ${new Date(filters.start_date).toLocaleDateString()}`}
              {filters.start_date && filters.end_date && ' - '}
              {filters.end_date && `To: ${new Date(filters.end_date).toLocaleDateString()}`}
            </p>
          )}
        </div>
        
        <div className="mb-6 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entry #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Debit</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Credit</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {entries && entries.length > 0 ? (
                entries.map((entry: any, index: number) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {new Date(entry.entry_date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer font-medium">
                        {entry.entry_number}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {entry.account_code} - {entry.account_name}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{entry.description}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">
                      {entry.debit_amount > 0 ? formatCurrency(entry.debit_amount) : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">
                      {entry.credit_amount > 0 ? formatCurrency(entry.credit_amount) : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">
                      {formatCurrency(entry.balance || 0)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-4 py-4 text-center text-sm text-gray-500">No ledger entries found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {pagination && pagination.total_pages > 1 && (
          <div className="mt-4 flex justify-between items-center">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Page {pagination.page} of {pagination.total_pages} ({pagination.total} entries)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  const newPage = pagination.page - 1
                  setReportFilters({ ...reportFilters, page: newPage.toString() })
                  handleLoadReport()
                }}
                disabled={pagination.page === 1}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => {
                  const newPage = pagination.page + 1
                  setReportFilters({ ...reportFilters, page: newPage.toString() })
                  handleLoadReport()
                }}
                disabled={pagination.page >= pagination.total_pages}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderJournalEntryListing = () => {
    if (!reportData) return null
    
    const { entries, pagination, filters } = reportData
    
    return (
      <div>
        <div className="mb-4">
          <h3 className="text-lg font-semibold">Journal Entry Listing</h3>
          {filters && (filters.start_date || filters.end_date) && (
            <p className="text-sm text-gray-500">
              {filters.start_date && `From: ${new Date(filters.start_date).toLocaleDateString()}`}
              {filters.start_date && filters.end_date && ' - '}
              {filters.end_date && `To: ${new Date(filters.end_date).toLocaleDateString()}`}
            </p>
          )}
        </div>
        
        <div className="mb-6 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entry #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Debit</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Credit</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {entries && entries.length > 0 ? (
                entries.map((entry: any) => (
                  <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer" onClick={() => handleTransactionClick({ 
                    id: entry.id || '',
                    journal_entry_id: entry.id || '',
                    account_id: '',
                    description: entry.description || '',
                    debit: 0,
                    credit: 0,
                    running_balance: 0,
                    entry_date: entry.entry_date || '',
                    created_at: entry.created_at || new Date().toISOString(),
                    journal_entry: { 
                      id: entry.id, 
                      entry_number: entry.entry_number,
                      entry_date: entry.entry_date || '',
                      description: entry.description || '',
                      status: entry.status || ''
                    } 
                  } as LedgerEntry)}>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {new Date(entry.entry_date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="text-blue-600 dark:text-blue-400 hover:underline font-medium">
                        {entry.entry_number}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{entry.description}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{entry.source_module || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">
                      {formatCurrency(entry.total_debit || 0)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">
                      {formatCurrency(entry.total_credit || 0)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        entry.status === 'posted' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                      }`}>
                        {entry.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-4 py-4 text-center text-sm text-gray-500">No journal entries found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {pagination && pagination.total_pages > 1 && (
          <div className="mt-4 flex justify-between items-center">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Page {pagination.page} of {pagination.total_pages} ({pagination.total} entries)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  const newPage = pagination.page - 1
                  setReportFilters({ ...reportFilters, page: newPage.toString() })
                  handleLoadReport()
                }}
                disabled={pagination.page === 1}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => {
                  const newPage = pagination.page + 1
                  setReportFilters({ ...reportFilters, page: newPage.toString() })
                  handleLoadReport()
                }}
                disabled={pagination.page >= pagination.total_pages}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderSalesByCategory = () => {
    if (!reportData) return null
    
    const { categories, order_types, totals, date_range } = reportData
    
    return (
      <div>
        <div className="mb-4">
          <h3 className="text-lg font-semibold">Sales by Category</h3>
          {date_range && (date_range.start_date || date_range.end_date) && (
            <p className="text-sm text-gray-500">
              {date_range.start_date && `From: ${new Date(date_range.start_date).toLocaleDateString()}`}
              {date_range.start_date && date_range.end_date && ' - '}
              {date_range.end_date && `To: ${new Date(date_range.end_date).toLocaleDateString()}`}
            </p>
          )}
        </div>
        
        {/* Summary Cards */}
        {totals && (
          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 dark:bg-blue-900 p-4 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">Total Sales</p>
              <p className="text-lg font-semibold text-blue-600 dark:text-blue-400">{formatCurrency(totals.total_sales || 0)}</p>
            </div>
            <div className="bg-green-50 dark:bg-green-900 p-4 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">Total Quantity</p>
              <p className="text-lg font-semibold text-green-600 dark:text-green-400">{totals.total_quantity || 0}</p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900 p-4 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">Total Orders</p>
              <p className="text-lg font-semibold text-purple-600 dark:text-purple-400">{totals.total_orders || 0}</p>
            </div>
          </div>
        )}
        
        {/* Sales by Category */}
        <div className="mb-6">
          <h4 className="text-md font-semibold mb-3">Sales by Category</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Sales</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Orders</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Average Price</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {categories && categories.length > 0 ? (
                  categories.map((cat: any, index: number) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{cat.category_name}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(cat.total_sales || 0)}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{cat.total_quantity || 0}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{cat.total_orders || 0}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(cat.average_price || 0)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-4 py-4 text-center text-sm text-gray-500">No sales data found</td>
                  </tr>
                )}
                {totals && (
                  <tr className="bg-gray-100 dark:bg-gray-600 font-bold cursor-default border-t-2">
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">Total</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">{formatCurrency(totals.total_sales || 0)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">{totals.total_quantity || 0}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">{totals.total_orders || 0}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">-</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Sales by Order Type */}
        {order_types && order_types.length > 0 && (
          <div className="mb-6">
            <h4 className="text-md font-semibold mb-3">Sales by Order Type</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order Type</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Sales</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Orders</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {order_types.map((type: any, index: number) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white capitalize">{type.order_type}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(type.total_sales || 0)}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{type.total_quantity || 0}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{type.total_orders || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderExpenseByCategory = () => {
    if (!reportData) return null
    
    const { categories, by_type, totals, date_range } = reportData
    
    return (
      <div>
        <div className="mb-4">
          <h3 className="text-lg font-semibold">Expense by Category</h3>
          {date_range && (date_range.start_date || date_range.end_date) && (
            <p className="text-sm text-gray-500">
              {date_range.start_date && `From: ${new Date(date_range.start_date).toLocaleDateString()}`}
              {date_range.start_date && date_range.end_date && ' - '}
              {date_range.end_date && `To: ${new Date(date_range.end_date).toLocaleDateString()}`}
            </p>
          )}
        </div>
        
        {/* Summary Cards */}
        {totals && (
          <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 dark:bg-blue-900 p-4 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">Total Expenses</p>
              <p className="text-lg font-semibold text-blue-600 dark:text-blue-400">{formatCurrency(totals.total_expenses || 0)}</p>
            </div>
            <div className="bg-green-50 dark:bg-green-900 p-4 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">Direct Expenses</p>
              <p className="text-lg font-semibold text-green-600 dark:text-green-400">{formatCurrency(totals.direct_total || 0)}</p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900 p-4 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">Indirect Expenses</p>
              <p className="text-lg font-semibold text-purple-600 dark:text-purple-400">{formatCurrency(totals.indirect_total || 0)}</p>
            </div>
            <div className="bg-orange-50 dark:bg-orange-900 p-4 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400">Total Count</p>
              <p className="text-lg font-semibold text-orange-600 dark:text-orange-400">{totals.total_count || 0}</p>
            </div>
          </div>
        )}
        
        {/* Expenses by Category */}
        <div className="mb-6">
          <h4 className="text-md font-semibold mb-3">Expenses by Category</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Amount</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Count</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Percentage</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {categories && categories.length > 0 ? (
                  categories.map((cat: any, index: number) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{cat.category_name}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(cat.total_amount || 0)}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{cat.count || 0}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{cat.percentage?.toFixed(2) || '0.00'}%</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-4 py-4 text-center text-sm text-gray-500">No expense data found</td>
                  </tr>
                )}
                {totals && (
                  <tr className="bg-gray-100 dark:bg-gray-600 font-bold cursor-default border-t-2">
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">Total</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">{formatCurrency(totals.total_expenses || 0)}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">{totals.total_count || 0}</td>
                    <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">100.00%</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Expenses by Type (Direct/Indirect) */}
        {by_type && (
          <div className="mb-6">
            <h4 className="text-md font-semibold mb-3">Expenses by Type</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-green-50 dark:bg-green-900 p-4 rounded-lg">
                <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Direct Expenses</h5>
                <p className="text-lg font-semibold text-green-600 dark:text-green-400">{formatCurrency(by_type.direct?.total || 0)}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{by_type.direct?.count || 0} expenses</p>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900 p-4 rounded-lg">
                <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Indirect Expenses</h5>
                <p className="text-lg font-semibold text-purple-600 dark:text-purple-400">{formatCurrency(by_type.indirect?.total || 0)}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{by_type.indirect?.count || 0} expenses</p>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderBalanceSheet = () => {
    if (!reportData) return null
    
    return (
      <div>
        <div className="mb-4">
          <h3 className="text-lg font-semibold">Balance Sheet</h3>
          {reportData.as_of_date && (
            <p className="text-sm text-gray-500">
              As of: {new Date(reportData.as_of_date).toLocaleDateString()}
            </p>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Assets */}
          <div>
            <h4 className="text-md font-semibold mb-3">Assets</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {/* Current Assets */}
                  {reportData.current_assets && reportData.current_assets.length > 0 && (
                    <>
                      <tr className="bg-gray-50 dark:bg-gray-700">
                        <td colSpan={2} className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                          Current Assets
                        </td>
                      </tr>
                      {reportData.current_assets.map((asset: any) => (
                        <tr 
                          key={asset.id}
                          className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleAccountClick(asset)}
                        >
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white pl-8">{asset.account_code} - {asset.account_name}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(asset.balance)}</td>
                        </tr>
                      ))}
                      {reportData.totals && (
                        <tr className="bg-gray-100 dark:bg-gray-600 font-semibold cursor-default">
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 pl-8">Total Current Assets</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">{formatCurrency(reportData.totals.total_current_assets || 0)}</td>
                        </tr>
                      )}
                    </>
                  )}
                  
                  {/* Non-Current Assets */}
                  {reportData.non_current_assets && reportData.non_current_assets.length > 0 && (
                    <>
                      <tr className="bg-gray-50 dark:bg-gray-700">
                        <td colSpan={2} className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                          Non-Current Assets
                        </td>
                      </tr>
                      {reportData.non_current_assets.map((asset: any) => (
                        <tr 
                          key={asset.id}
                          className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleAccountClick(asset)}
                        >
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white pl-8">{asset.account_code} - {asset.account_name}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(asset.balance)}</td>
                        </tr>
                      ))}
                      {reportData.totals && (
                        <tr className="bg-gray-100 dark:bg-gray-600 font-semibold cursor-default">
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 pl-8">Total Non-Current Assets</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">{formatCurrency(reportData.totals.total_non_current_assets || 0)}</td>
                        </tr>
                      )}
                    </>
                  )}
                  
                  {(!reportData.current_assets || reportData.current_assets.length === 0) && 
                   (!reportData.non_current_assets || reportData.non_current_assets.length === 0) && (
                    <tr>
                      <td colSpan={2} className="px-4 py-4 text-center text-sm text-gray-500">No asset data</td>
                    </tr>
                  )}
                  
                  {reportData.totals && (
                    <tr className="bg-gray-200 dark:bg-gray-500 font-bold cursor-default border-t-2 border-gray-300 dark:border-gray-400">
                      <td className="px-4 py-3 text-sm text-gray-800 dark:text-gray-100">Total Assets</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-800 dark:text-gray-100">{formatCurrency(reportData.totals.total_assets)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Liabilities & Equity */}
          <div>
            <h4 className="text-md font-semibold mb-3">Liabilities & Equity</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {/* Liabilities */}
                  {reportData.current_liabilities && reportData.current_liabilities.length > 0 && (
                    <>
                      <tr className="bg-gray-50 dark:bg-gray-700">
                        <td colSpan={2} className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                          Current Liabilities
                        </td>
                      </tr>
                      {reportData.current_liabilities.map((liability: any) => (
                        <tr 
                          key={liability.id}
                          className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleAccountClick(liability)}
                        >
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white pl-8">{liability.account_code} - {liability.account_name}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(liability.balance)}</td>
                        </tr>
                      ))}
                      {reportData.totals && (
                        <tr className="bg-gray-100 dark:bg-gray-600 font-semibold cursor-default">
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 pl-8">Total Current Liabilities</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">{formatCurrency(reportData.totals.total_current_liabilities || 0)}</td>
                        </tr>
                      )}
                    </>
                  )}
                  
                  {/* Non-Current Liabilities */}
                  {reportData.non_current_liabilities && reportData.non_current_liabilities.length > 0 && (
                    <>
                      <tr className="bg-gray-50 dark:bg-gray-700">
                        <td colSpan={2} className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                          Non-Current Liabilities
                        </td>
                      </tr>
                      {reportData.non_current_liabilities.map((liability: any) => (
                        <tr 
                          key={liability.id}
                          className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleAccountClick(liability)}
                        >
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white pl-8">{liability.account_code} - {liability.account_name}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(liability.balance)}</td>
                        </tr>
                      ))}
                      {reportData.totals && (
                        <tr className="bg-gray-100 dark:bg-gray-600 font-semibold cursor-default">
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 pl-8">Total Non-Current Liabilities</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">{formatCurrency(reportData.totals.total_non_current_liabilities || 0)}</td>
                        </tr>
                      )}
                    </>
                  )}
                  
                  {reportData.totals && (
                    <tr className="bg-gray-100 dark:bg-gray-600 font-semibold cursor-default border-t-2 border-gray-300 dark:border-gray-400">
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">Total Liabilities</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">{formatCurrency(reportData.totals.total_liabilities)}</td>
                    </tr>
                  )}
                  
                  {/* Equity */}
                  {reportData.equity && reportData.equity.length > 0 && (
                    <>
                      {reportData.equity.map((eq: any) => (
                        <tr 
                          key={eq.id}
                          className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleAccountClick(eq)}
                        >
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{eq.account_code} - {eq.account_name}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(eq.balance)}</td>
                        </tr>
                      ))}
                      {reportData.totals && reportData.totals.net_income !== 0 && (
                        <tr>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">Net Income</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(reportData.totals.net_income)}</td>
                        </tr>
                      )}
                      {reportData.totals && (
                        <tr className="bg-gray-100 dark:bg-gray-600 font-semibold cursor-default">
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">Total Equity</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-200">{formatCurrency(reportData.totals.total_equity + (reportData.totals.net_income || 0))}</td>
                        </tr>
                      )}
                    </>
                  )}
                  
                  {reportData.totals && (
                    <tr className="bg-gray-200 dark:bg-gray-500 font-bold cursor-default border-t-2 border-gray-300 dark:border-gray-400">
                      <td className="px-4 py-3 text-sm text-gray-800 dark:text-gray-100">Total Liabilities & Equity</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-800 dark:text-gray-100">{formatCurrency(reportData.totals.total_liabilities_and_equity)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const handleSaveExpense = async () => {
    if (savingExpense) return // Prevent double submission
    
    try {
      setSavingExpense(true)
      
      // Validate expense items
      if (expenseItems.length === 0) {
        showToastMessage('Please add at least one expense item', 'error')
        setSavingExpense(false)
        return
      }
      
      // Validate each item
      for (let i = 0; i < expenseItems.length; i++) {
        const item = expenseItems[i]
        if (!item.item_name || item.item_name.trim() === '') {
          showToastMessage(`Please enter item name for item ${i + 1}`, 'error')
          setSavingExpense(false)
          return
        }
        if (!item.unit_price || item.unit_price <= 0) {
          showToastMessage(`Please enter a valid unit price for item ${i + 1}`, 'error')
          setSavingExpense(false)
          return
        }
      }

      // Use category_id directly from form
      const category_id = expenseForm.category_id || null

      // Calculate totals
      const totals = calculateBillTotals()
      const paidAmount = parseFloat(expenseForm.paid_amount) || 0
      
      // Determine payment status
      let paymentStatus = expenseForm.payment_status
      if (paidAmount > 0 && paidAmount < totals.total_amount) {
        paymentStatus = 'partial'
      } else if (paidAmount >= totals.total_amount) {
        paymentStatus = 'paid'
      } else {
        paymentStatus = 'unpaid'
      }

      // If unpaid, payment method must be 'none'
      // If paid/partial, payment method cannot be 'none'
      let paymentMethod = expenseForm.payment_method
      if (paymentStatus === 'unpaid') {
        paymentMethod = 'none'
      } else if ((paymentStatus === 'paid' || paymentStatus === 'partial') && paymentMethod === 'none') {
        paymentMethod = 'cash' // Default to cash if somehow none is selected
      }

      // If vendor is manually entered, save vendor_name
      const payeeName = expenseForm.vendor_id 
        ? expenseForm.vendor_name 
        : (vendorSearchTerm || expenseForm.vendor_name || expenseForm.description || 'Unknown')

      // Prepare expense items data - ensure item_name is not empty
      const itemsData = expenseItems.map(item => {
        const itemData = {
          item_name: (item.item_name || '').trim() || 'Expense Item', // Ensure not empty
          item_description: (item.item_description || '').trim() || null,
          quantity: typeof item.quantity === 'string' ? parseFloat(item.quantity) || 1 : (item.quantity || 1),
          unit_price: typeof item.unit_price === 'string' ? parseFloat(item.unit_price) || 0 : (item.unit_price || 0),
          tax_rate: typeof item.tax_rate === 'string' ? parseFloat(item.tax_rate) || 0 : (item.tax_rate || 0),
          tax_amount: typeof item.tax_amount === 'string' ? parseFloat(item.tax_amount) || 0 : (item.tax_amount || 0),
          item_discount: typeof item.item_discount === 'string' ? parseFloat(item.item_discount) || 0 : (item.item_discount || 0),
          item_discount_type: item.item_discount_type || 'amount',
          subtotal: typeof item.subtotal === 'string' ? parseFloat(item.subtotal) || 0 : (item.subtotal || 0),
          total_amount: typeof item.total_amount === 'string' ? parseFloat(item.total_amount) || 0 : (item.total_amount || 0),
          link_to_inventory: item.link_to_inventory || false,
          inventory_id: item.inventory_id || null
        }

        return itemData
      })

      const data = {
        expense_date: expenseForm.expense_date,
        due_date: expenseForm.due_date || null,
        description: expenseForm.description || 'Expense',
        category_id: category_id,
        payee_name: payeeName,
        payment_method: paymentMethod,
        vendor_id: expenseForm.vendor_id || null,
        vendor_name: payeeName, // Save vendor name for manual entries
        vat_number: expenseForm.vat_number || null,
        payment_status: paymentStatus,
        paid_amount: paidAmount,
        bill_discount: totals.bill_discount,
        bill_discount_type: expenseForm.bill_discount_type,
        subtotal: totals.subtotal,
        tax_amount: totals.tax_amount,
        total_amount: totals.total_amount,
        expense_items: itemsData
      }



      let response
      if (modalType === 'add') {
        response = await apiService.createExpense(data)
      } else {
        response = await apiService.updateExpense(editingItem.id, data)
      }

      if (response.success) {
        showToastMessage(
          modalType === 'add' ? 'Expense created successfully' : 'Expense updated successfully',
          'success'
        )
        setShowModal(false)
        // Reload expenses to get the latest data with items
        await loadData()
      } else {
        showToastMessage(response.error || 'Failed to save expense', 'error')
      }
    } catch (error) {
      console.error('Error saving expense:', error)
      showToastMessage('An error occurred', 'error')
    } finally {
      setSavingExpense(false)
    }
  }

  const handleRecordPayment = (expense: Expense) => {
    setExpenseForPayment(expense)
    setPaymentForm({
      payment_date: new Date().toISOString().split('T')[0],
      payment_amount: '',
      payment_method: 'cash',
      payment_reference: '',
      notes: ''
    })
    setShowPaymentModal(true)
  }

  const handleSavePayment = async () => {
    if (!expenseForPayment || savingPayment) return
    
    try {
      setSavingPayment(true)
      
      // Validate payment amount
      const paymentAmount = parseFloat(paymentForm.payment_amount)
      if (!paymentAmount || paymentAmount <= 0) {
        showToastMessage('Please enter a valid payment amount', 'error')
        setSavingPayment(false)
        return
      }
      
      const outstanding = (expenseForPayment.total_amount || 0) - (expenseForPayment.paid_amount || 0)
      if (paymentAmount > outstanding) {
        showToastMessage(`Payment amount cannot exceed outstanding amount of ${formatCurrency(outstanding)}`, 'error')
        setSavingPayment(false)
        return
      }
      
      // Call API to record payment
      const response = await apiService.recordExpensePayment(expenseForPayment.id, {
        payment_date: paymentForm.payment_date,
        payment_amount: paymentAmount,
        payment_method: paymentForm.payment_method,
        payment_reference: paymentForm.payment_reference || null,
        notes: paymentForm.notes || null
      })
      
      if (response.success) {
        showToastMessage('Payment recorded successfully', 'success')
        setShowPaymentModal(false)
        setExpenseForPayment(null)
        // Reload expenses to get updated payment status
        await loadData()
      } else {
        showToastMessage(response.error || 'Failed to record payment', 'error')
      }
    } catch (error) {
      console.error('Error recording payment:', error)
      showToastMessage('An error occurred while recording payment', 'error')
    } finally {
      setSavingPayment(false)
    }
  }

  const handleDeleteExpense = (expense: Expense) => {
    setExpenseToDelete(expense)
    setShowDeleteExpenseModal(true)
  }

  const confirmDeleteExpense = async () => {
    if (!expenseToDelete) return

    try {
      const response = await apiService.deleteExpense(expenseToDelete.id)
      if (response.success) {
        showToastMessage('Expense deleted successfully', 'success')
        loadData()
        setShowDeleteExpenseModal(false)
        setExpenseToDelete(null)
      } else {
        showToastMessage(response.error || 'Failed to delete expense', 'error')
      }
    } catch (error) {
      console.error('Error deleting expense:', error)
      showToastMessage('An error occurred', 'error')
    }
  }

  // Journal Entry handlers
  const handleAddJournalEntry = () => {
    setModalType('add')
    setEditingItem(null)
    setJournalForm({
      entry_date: new Date().toISOString().split('T')[0],
      description: '',
      journal_lines: [{ account_id: '', description: '', debit_amount: '', credit_amount: '' }]
    })
    setShowModal(true)
  }

  const handleViewJournalEntry = async (id: string) => {
    setJournalDetailLoading(true)
    setShowJournalDetailModal(true)
    try {
      const response = await apiService.getJournalEntryById(id)
      if (response.success && response.data) {
        setViewingJournalEntry(response.data as JournalEntry)
      } else {
        showToastMessage(response.error || 'Failed to load journal entry', 'error')
        setShowJournalDetailModal(false)
      }
    } catch (error) {
      console.error('Error loading journal entry:', error)
      showToastMessage('Failed to load journal entry', 'error')
      setShowJournalDetailModal(false)
    } finally {
      setJournalDetailLoading(false)
    }
  }

  const handleApproveJournalEntry = async () => {
    if (!viewingJournalEntry || viewingJournalEntry.status !== 'draft') return
    try {
      const response = await apiService.approveJournalEntry(viewingJournalEntry.id)
      if (response.success && response.data) {
        setViewingJournalEntry({ ...viewingJournalEntry, status: 'posted' })
        showToastMessage('Journal entry approved and posted. It will now appear in Trial Balance and P&L.', 'success')
        loadData()
      } else {
        showToastMessage(response.error || 'Failed to approve journal entry', 'error')
      }
    } catch (error) {
      console.error('Error approving journal entry:', error)
      showToastMessage('Failed to approve journal entry', 'error')
    }
  }

  const handleDeleteJournalEntry = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this journal entry? This cannot be undone.')) return
    try {
      const response = await apiService.deleteJournalEntry(id, user?.id)
      if (response.success) {
        showToastMessage((response as { message?: string }).message || 'Journal entry deleted successfully', 'success')
        setJournalEntries(prev => prev.filter(e => e.id !== id))
        setFilteredJournalEntries(prev => prev.filter(e => e.id !== id))
        if (viewingJournalEntry?.id === id) {
          setShowJournalDetailModal(false)
          setViewingJournalEntry(null)
        }
        loadData()
      } else {
        showToastMessage(response.error || 'Failed to delete journal entry', 'error')
      }
    } catch (error) {
      console.error('Error deleting journal entry:', error)
      showToastMessage('Failed to delete journal entry', 'error')
    }
  }

  const handleAddJournalLine = () => {
    setJournalForm({
      ...journalForm,
      journal_lines: [...journalForm.journal_lines, { account_id: '', description: '', debit_amount: '', credit_amount: '' }]
    })
  }

  const handleRemoveJournalLine = (index: number) => {
    const newLines = journalForm.journal_lines.filter((_, i) => i !== index)
    setJournalForm({ ...journalForm, journal_lines: newLines })
  }

  const handleUpdateJournalLine = (index: number, field: string, value: string) => {
    const newLines = [...journalForm.journal_lines]
    newLines[index] = { ...newLines[index], [field]: value }
    setJournalForm({ ...journalForm, journal_lines: newLines })
  }

  const handleSaveJournalEntry = async () => {
    try {
      if (!journalForm.description || !journalForm.entry_date) {
        showToastMessage('Please fill in all required fields', 'error')
        return
      }

      // Validate that all lines have account_id
      const missingAccounts = journalForm.journal_lines.filter(line => !line.account_id)
      if (missingAccounts.length > 0) {
        showToastMessage('Please select an account for all journal lines', 'error')
        return
      }

      // Check that each line has either debit or credit (not both, not neither)
      const invalidLines = journalForm.journal_lines.filter(line => {
        const debit = parseFloat(line.debit_amount) || 0
        const credit = parseFloat(line.credit_amount) || 0
        return (debit > 0 && credit > 0) || (debit === 0 && credit === 0)
      })
      if (invalidLines.length > 0) {
        showToastMessage('Each line must have either a debit OR a credit amount (not both, not neither)', 'error')
        return
      }

      // Check that debits equal credits
      const totalDebit = journalForm.journal_lines.reduce((sum, line) => sum + (parseFloat(line.debit_amount) || 0), 0)
      const totalCredit = journalForm.journal_lines.reduce((sum, line) => sum + (parseFloat(line.credit_amount) || 0), 0)
      
      if (totalDebit !== totalCredit) {
        showToastMessage('Total debits must equal total credits', 'error')
        return
      }

      if (totalDebit === 0) {
        showToastMessage('At least one debit or credit amount is required', 'error')
        return
      }

      // Prepare journal entry lines
      const lines = journalForm.journal_lines.map((line, index) => ({
        account_id: line.account_id,
        description: line.description || '',
        narration: line.description || '',
        debit_amount: parseFloat(line.debit_amount) || 0,
        credit_amount: parseFloat(line.credit_amount) || 0,
        sort_order: index
      }))

      const data = {
        entry_date: journalForm.entry_date,
        description: journalForm.description,
        total_debit: totalDebit,
        total_credit: totalCredit,
        source_module: 'manual',
        status: 'draft',
        lines: lines
      }

      const response = await apiService.createJournalEntry(data)
      if (response.success) {
        showToastMessage('Journal entry created successfully', 'success')
        setShowModal(false)
        loadData()
      } else {
        showToastMessage(response.error || 'Failed to save journal entry', 'error')
      }
    } catch (error) {
      console.error('Error saving journal entry:', error)
      showToastMessage('An error occurred', 'error')
    }
  }

  const handleCancelModal = () => {
    setShowModal(false)
    setEditingItem(null)
  }

  const renderModal = () => {
    if (!showModal) return null

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={handleCancelModal}></div>
          <div className={`relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full ${activeTab === 'vendors' || activeTab === 'expenses' ? 'max-w-2xl' : 'max-w-md'}`}>
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {modalType === 'add' ? 'Add' : 'Edit'} {
                  activeTab === 'chart' ? 'Account' : 
                  activeTab === 'vendors' ? 'Vendor' : 
                  activeTab === 'journal' ? 'Journal Entry' : 
                  'Expense'}
              </h3>
              <button onClick={handleCancelModal} className="text-gray-400 hover:text-gray-500">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              {activeTab === 'chart' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Account Code *
                    </label>
                    <input
                      type="text"
                      value={chartForm.account_code}
                      onChange={(e) => setChartForm({...chartForm, account_code: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Account Name *
                    </label>
                    <input
                      type="text"
                      value={chartForm.account_name}
                      onChange={(e) => setChartForm({...chartForm, account_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Account Type *
                    </label>
                    <select
                      value={chartForm.account_type}
                      onChange={(e) => setChartForm({...chartForm, account_type: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    >
                      <option value="asset">Asset</option>
                      <option value="liability">Liability</option>
                      <option value="equity">Equity</option>
                      <option value="revenue">Revenue</option>
                      <option value="expense">Expense</option>
                    </select>
                  </div>
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={chartForm.is_active}
                        onChange={(e) => setChartForm({...chartForm, is_active: e.target.checked})}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Active</span>
                    </label>
                  </div>
                </div>
              )}

              {activeTab === 'vendors' && (
                <div className="space-y-4 max-h-[70vh] overflow-y-auto">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Vendor Name *
                    </label>
                    <input
                      type="text"
                      value={vendorForm.name || vendorForm.vendor_name}
                      onChange={(e) => setVendorForm({...vendorForm, name: e.target.value, vendor_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Contact Person
                    </label>
                    <input
                      type="text"
                      value={vendorForm.contact_person}
                      onChange={(e) => setVendorForm({...vendorForm, contact_person: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Phone *
                    </label>
                    <input
                      type="text"
                      value={vendorForm.phone}
                      onChange={(e) => setVendorForm({...vendorForm, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={vendorForm.email}
                      onChange={(e) => setVendorForm({...vendorForm, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Address
                    </label>
                    <textarea
                      value={vendorForm.address}
                      onChange={(e) => setVendorForm({...vendorForm, address: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                      rows={2}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        City
                      </label>
                      <input
                        type="text"
                        value={vendorForm.city}
                        onChange={(e) => setVendorForm({...vendorForm, city: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        State
                      </label>
                      <input
                        type="text"
                        value={vendorForm.state}
                        onChange={(e) => setVendorForm({...vendorForm, state: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Postal Code
                    </label>
                    <input
                      type="text"
                      value={vendorForm.postal_code || vendorForm.zip_code}
                      onChange={(e) => setVendorForm({...vendorForm, postal_code: e.target.value, zip_code: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Company CR (Commercial Registration)
                    </label>
                    <input
                      type="text"
                      value={vendorForm.cr_number}
                      onChange={(e) => setVendorForm({...vendorForm, cr_number: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                      placeholder="e.g., 1234567890"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      VAT Number
                    </label>
                    <input
                      type="text"
                      value={vendorForm.tax_number}
                      onChange={(e) => setVendorForm({...vendorForm, tax_number: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                      placeholder="e.g., 310123456700003"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      National Address
                    </label>
                    <textarea
                      value={vendorForm.national_address}
                      onChange={(e) => setVendorForm({...vendorForm, national_address: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                      rows={2}
                      placeholder="Saudi Arabia national address format"
                    />
                  </div>
                </div>
              )}

              {activeTab === 'expenses' && (
                <div className="space-y-4 max-h-[80vh] overflow-y-auto">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Expense Date *
                      </label>
                      <input
                        type="date"
                        value={expenseForm.expense_date}
                        onChange={(e) => setExpenseForm({...expenseForm, expense_date: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Due Date
                      </label>
                      <input
                        type="date"
                        value={expenseForm.due_date}
                        onChange={(e) => setExpenseForm({...expenseForm, due_date: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Expense Type
                    </label>
                    <select
                      value={expenseForm.category_id}
                      onChange={(e) => setExpenseForm({...expenseForm, category_id: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    >
                      <option value="">Select an expense type</option>
                      {expenseCategories
                        .filter(cat => cat.is_active !== false)
                        .map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.category_name}
                            {category.chart_of_accounts ? ` (${category.chart_of_accounts.account_code})` : ''}
                          </option>
                        ))}
                    </select>
                    {expenseForm.category_id && (() => {
                      const selectedCategory = expenseCategories.find(cat => cat.id === expenseForm.category_id)
                      return selectedCategory?.chart_of_accounts ? (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Linked to: {selectedCategory.chart_of_accounts.account_code} - {selectedCategory.chart_of_accounts.account_name}
                        </p>
                      ) : null
                    })()}
                  </div>

                  {/* Vendor Search */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Vendor / Payee
                    </label>
                    <input
                      type="text"
                      value={expenseForm.vendor_id ? (expenseForm.vendor_name || vendorSearchTerm) : vendorSearchTerm}
                      onChange={(e) => {
                        const value = e.target.value
                        setVendorSearchTerm(value)
                        if (!value) {
                          setExpenseForm({...expenseForm, vendor_id: '', vendor_name: '', vat_number: ''})
                        }
                      }}
                      onFocus={() => {
                        if (vendors.length > 0) {
                          setShowVendorDropdown(true)
                        }
                      }}
                      onBlur={() => {
                        // Delay to allow dropdown click
                        setTimeout(() => setShowVendorDropdown(false), 200)
                      }}
                      placeholder="Search vendor or enter manually"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    />
                    {showVendorDropdown && filteredVendorsForExpense.length > 0 && (
                      <div 
                        className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto"
                        onMouseDown={(e) => e.preventDefault()} // Prevent blur on dropdown click
                      >
                        {filteredVendorsForExpense.map((vendor) => (
                          <div
                            key={vendor.id}
                            onClick={() => handleVendorSelect(vendor)}
                            className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                          >
                            <div className="font-medium">{vendor.name || vendor.vendor_name}</div>
                            <div className="text-sm text-gray-500">{vendor.phone}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {vendorSearchTerm && !expenseForm.vendor_id && (
                      <div className="mt-1 text-xs text-gray-500">
                        Entering manually: {vendorSearchTerm}
                      </div>
                    )}
                  </div>

                  {/* VAT Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      VAT Number (Optional)
                    </label>
                    <input
                      type="text"
                      value={expenseForm.vat_number}
                      onChange={(e) => setExpenseForm({...expenseForm, vat_number: e.target.value})}
                      placeholder="Auto-filled from vendor or enter manually"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </label>
                    <input
                      type="text"
                      value={expenseForm.description}
                      onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>

                  {/* Expense Items */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Expense Items *
                      </label>
                      <button
                        type="button"
                        onClick={handleAddExpenseItem}
                        className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
                      >
                        + Add Item
                      </button>
                    </div>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {expenseItems.map((item, index) => (
                        <div key={index} className="p-3 border border-gray-200 dark:border-gray-600 rounded-md">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Item {index + 1}</span>
                            {expenseItems.length > 1 && (
                              <button
                                type="button"
                                onClick={() => handleRemoveExpenseItem(index)}
                                className="text-xs text-red-600 hover:text-red-700"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="col-span-2">
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                Item Name *
                              </label>
                              <input
                                type="text"
                                value={item.item_name}
                                onChange={(e) => handleUpdateExpenseItem(index, 'item_name', e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                Quantity
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                value={item.quantity}
                                onChange={(e) => handleUpdateExpenseItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                Unit Price *
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                value={item.unit_price}
                                onChange={(e) => handleUpdateExpenseItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                Tax Rate (%)
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                value={item.tax_rate}
                                onChange={(e) => handleUpdateExpenseItem(index, 'tax_rate', parseFloat(e.target.value) || 0)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                Discount Type
                              </label>
                              <select
                                value={item.item_discount_type}
                                onChange={(e) => handleUpdateExpenseItem(index, 'item_discount_type', e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                              >
                                <option value="amount">Amount</option>
                                <option value="percentage">Percentage</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                Item Discount
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                value={item.item_discount}
                                onChange={(e) => handleUpdateExpenseItem(index, 'item_discount', parseFloat(e.target.value) || 0)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="flex items-center space-x-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={item.link_to_inventory || false}
                                  onChange={(e) => {
                                    const updatedItems = [...expenseItems]
                                    updatedItems[index].link_to_inventory = e.target.checked
                                    if (!e.target.checked) {
                                      updatedItems[index].inventory_id = undefined
                                      // Clear search term for this item
                                      setInventorySearchTerms({ ...inventorySearchTerms, [index]: '' })
                                    }
                                    setExpenseItems(updatedItems)
                                  }}
                                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                  Link to Inventory
                                </span>
                              </label>
                            </div>
                            {item.link_to_inventory && (
                              <div className="col-span-2 relative">
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                  Select Inventory Item
                                </label>
                                <input
                                  type="text"
                                  value={item.inventory_id 
                                    ? (inventoryItems.find(inv => inv.id === item.inventory_id)?.name || '') 
                                    : (inventorySearchTerms[index] || '')}
                                  onChange={(e) => {
                                    const value = e.target.value
                                    setInventorySearchTerms({ ...inventorySearchTerms, [index]: value })
                                    if (!value) {
                                      handleUpdateExpenseItem(index, 'inventory_id', undefined)
                                    }
                                    // Filter inventory items
                                    const filtered = inventoryItems.filter(invItem =>
                                      invItem.name.toLowerCase().includes(value.toLowerCase()) ||
                                      (invItem.category || '').toLowerCase().includes(value.toLowerCase())
                                    )
                                    setFilteredInventoryItems({ ...filteredInventoryItems, [index]: filtered })
                                    setShowInventoryDropdowns({ ...showInventoryDropdowns, [index]: true })
                                  }}
                                  onFocus={() => {
                                    if (inventoryItems.length > 0) {
                                      const searchTerm = inventorySearchTerms[index] || ''
                                      const filtered = searchTerm
                                        ? inventoryItems.filter(invItem =>
                                            invItem.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                            (invItem.category || '').toLowerCase().includes(searchTerm.toLowerCase())
                                          )
                                        : inventoryItems
                                      setFilteredInventoryItems({ ...filteredInventoryItems, [index]: filtered })
                                      setShowInventoryDropdowns({ ...showInventoryDropdowns, [index]: true })
                                    }
                                  }}
                                  onBlur={() => {
                                    // Delay to allow dropdown click
                                    setTimeout(() => {
                                      setShowInventoryDropdowns({ ...showInventoryDropdowns, [index]: false })
                                    }, 200)
                                  }}
                                  placeholder="Search inventory item or leave empty to create new"
                                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                                />
                                {showInventoryDropdowns[index] && (filteredInventoryItems[index] || inventoryItems).length > 0 && (
                                  <div 
                                    className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto"
                                    onMouseDown={(e) => e.preventDefault()} // Prevent blur on dropdown click
                                  >
                                    {(filteredInventoryItems[index] || inventoryItems).map((invItem) => (
                                      <div
                                        key={invItem.id}
                                        onClick={() => {
                                          handleUpdateExpenseItem(index, 'inventory_id', invItem.id)
                                          setInventorySearchTerms({ ...inventorySearchTerms, [index]: invItem.name })
                                          setShowInventoryDropdowns({ ...showInventoryDropdowns, [index]: false })
                                        }}
                                        className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                                      >
                                        <div className="font-medium text-sm">{invItem.name}</div>
                                        <div className="text-xs text-gray-500">
                                          Stock: {invItem.currentStock} {invItem.unit}
                                          {invItem.category && ` • ${invItem.category}`}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {!item.inventory_id && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    Leave empty to create new inventory item: "{item.item_name}"
                                  </p>
                                )}
                              </div>
                            )}
                            <div className="col-span-2 text-xs text-gray-500 mt-1 pt-2 border-t border-gray-200 dark:border-gray-600">
                              <div className="flex justify-between">
                                <span>Subtotal: <strong>{item.subtotal.toFixed(2)}</strong></span>
                                <span>Tax: <strong>{item.tax_amount.toFixed(2)}</strong></span>
                                <span>Total: <strong>{item.total_amount.toFixed(2)}</strong></span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Bill Discount */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Bill Discount Type
                      </label>
                      <select
                        value={expenseForm.bill_discount_type}
                        onChange={(e) => setExpenseForm({...expenseForm, bill_discount_type: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                      >
                        <option value="amount">Amount</option>
                        <option value="percentage">Percentage</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Bill Discount
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={expenseForm.bill_discount}
                        onChange={(e) => setExpenseForm({...expenseForm, bill_discount: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                      />
                    </div>
                  </div>

                  {/* Totals */}
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                    {(() => {
                      const totals = calculateBillTotals()
                      return (
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span>Subtotal:</span>
                            <span>{totals.subtotal.toFixed(2)}</span>
                          </div>
                          {totals.bill_discount > 0 && (
                            <div className="flex justify-between text-red-600">
                              <span>Bill Discount:</span>
                              <span>-{totals.bill_discount.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span>Total VAT:</span>
                            <span>{totals.tax_amount.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
                            <span>Total Amount:</span>
                            <span>{totals.total_amount.toFixed(2)}</span>
                          </div>
                        </div>
                      )
                    })()}
                  </div>

                  {/* Payment Status */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Payment Status
                      </label>
                      <select
                        value={expenseForm.payment_status}
                        onChange={(e) => {
                          const newStatus = e.target.value
                          // If unpaid, set payment method to none
                          // If paid/partial and current method is none, set to cash
                          let newPaymentMethod = expenseForm.payment_method
                          if (newStatus === 'unpaid') {
                            newPaymentMethod = 'none'
                          } else if ((newStatus === 'paid' || newStatus === 'partial') && expenseForm.payment_method === 'none') {
                            newPaymentMethod = 'cash'
                          }
                          setExpenseForm({
                            ...expenseForm, 
                            payment_status: newStatus,
                            payment_method: newPaymentMethod,
                            // Also clear paid amount if unpaid
                            paid_amount: newStatus === 'unpaid' ? '' : expenseForm.paid_amount
                          })
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                      >
                        <option value="unpaid">Unpaid</option>
                        <option value="partial">Partially Paid</option>
                        <option value="paid">Paid</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Paid Amount
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={expenseForm.paid_amount}
                        onChange={(e) => setExpenseForm({...expenseForm, paid_amount: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                        disabled={expenseForm.payment_status === 'unpaid'}
                      />
                    </div>
                  </div>

                  {/* Payment Method */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Payment Method
                    </label>
                    <select
                      value={expenseForm.payment_method}
                      onChange={(e) => setExpenseForm({...expenseForm, payment_method: e.target.value})}
                      disabled={expenseForm.payment_status === 'unpaid'}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 ${
                        expenseForm.payment_status === 'unpaid' ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {expenseForm.payment_status === 'unpaid' && (
                        <option value="none">None</option>
                      )}
                      <option value="cash">Cash</option>
                      <option value="card">Card</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="check">Check</option>
                    </select>
                    {expenseForm.payment_status === 'unpaid' && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Payment method will be set when expense is paid
                      </p>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'journal' && (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Entry Date *
                    </label>
                    <input
                      type="date"
                      value={journalForm.entry_date}
                      onChange={(e) => setJournalForm({...journalForm, entry_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description *
                    </label>
                    <input
                      type="text"
                      value={journalForm.description}
                      onChange={(e) => setJournalForm({...journalForm, description: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                  
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <div className="flex justify-between items-center mb-3">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Journal Lines *
                      </label>
                      <button
                        type="button"
                        onClick={handleAddJournalLine}
                        className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
                      >
                        + Add Line
                      </button>
                    </div>
                    
                    {journalForm.journal_lines.map((line, index) => (
                      <div key={index} className="mb-4 p-3 border border-gray-200 dark:border-gray-600 rounded-md">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                            Line {index + 1}
                          </span>
                          {journalForm.journal_lines.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveJournalLine(index)}
                              className="text-xs text-red-600 hover:text-red-700"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                              Account <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={line.account_id}
                              onChange={(e) => handleUpdateJournalLine(index, 'account_id', e.target.value)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                              required
                            >
                              <option value="">Select Account</option>
                              {chartOfAccounts.map((account) => (
                                <option key={account.id} value={account.id}>
                                  {account.account_code} - {account.account_name}
                                </option>
                              ))}
                            </select>
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                              Description
                            </label>
                            <input
                              type="text"
                              value={line.description || ''}
                              onChange={(e) => handleUpdateJournalLine(index, 'description', e.target.value)}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                              placeholder="Optional description for this line"
                            />
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                Debit Amount
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                value={line.debit_amount}
                                onChange={(e) => handleUpdateJournalLine(index, 'debit_amount', e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                Credit Amount
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                value={line.credit_amount}
                                onChange={(e) => handleUpdateJournalLine(index, 'credit_amount', e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      <p>* Total debits must equal total credits</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700 gap-3">
              <button
                onClick={handleCancelModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (activeTab === 'chart') handleSaveChartAccount()
                  else if (activeTab === 'vendors') handleSaveVendor()
                  else if (activeTab === 'expenses') handleSaveExpense()
                  else if (activeTab === 'journal') handleSaveJournalEntry()
                }}
                disabled={activeTab === 'expenses' && savingExpense}
                className={`px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 ${
                  activeTab === 'expenses' && savingExpense ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {activeTab === 'expenses' && savingExpense ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Expense View Modal
  const renderExpenseViewModal = () => {
    if (!showViewModal || !viewingExpense) return null

    const expense = viewingExpense
    const items = expense.expense_items || []
    


    
    const totals = items.reduce((acc, item) => {
      acc.subtotal += (typeof item.subtotal === 'string' ? parseFloat(item.subtotal) : item.subtotal) || 0
      acc.tax += (typeof item.tax_amount === 'string' ? parseFloat(item.tax_amount) : item.tax_amount) || 0
      acc.total += (typeof item.total_amount === 'string' ? parseFloat(item.total_amount) : item.total_amount) || 0
      return acc
    }, { subtotal: 0, tax: 0, total: 0 })

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowViewModal(false)}></div>
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Expense Details - {expense.expense_number || 'N/A'}
              </h3>
              <button 
                onClick={() => setShowViewModal(false)} 
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Basic Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Expense Date</label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {new Date(expense.expense_date).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Due Date</label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {expense.due_date ? new Date(expense.due_date).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Category</label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {expense.category || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Vendor / Payee</label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {expense.vendor_name || expense.payee_name || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">VAT Number</label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {expense.vat_number || 'N/A'}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Description</label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {expense.description || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Expense Items */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Expense Items</h4>
                {items.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item Name</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tax Rate</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subtotal</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tax</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {items.map((item: any, index: number) => (
                          <tr key={item.id || index}>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                              {item.item_name || 'N/A'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                              {item.quantity || 1}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                              {formatCurrency(item.unit_price || 0)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                              {item.tax_rate ? `${parseFloat(item.tax_rate).toFixed(2)}%` : '0%'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                              {item.item_discount > 0 ? (
                                <>
                                  {item.item_discount_type === 'percentage' ? `${item.item_discount}%` : formatCurrency(item.item_discount)}
                                </>
                              ) : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                              {formatCurrency(item.subtotal || 0)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                              {formatCurrency(item.tax_amount || 0)}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                              {formatCurrency(item.total_amount || 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <p>No expense items found for this expense.</p>
                    <p className="text-sm mt-2">This expense may have been created before the line items feature was added.</p>
                  </div>
                )}
              </div>

              {/* Totals */}
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Summary</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Items Subtotal:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(totals.subtotal)}</span>
                  </div>
                  {expense.bill_discount && expense.bill_discount > 0 && (
                    <div className="flex justify-between text-sm text-red-600">
                      <span>Bill Discount ({expense.bill_discount_type === 'percentage' ? `${expense.bill_discount}%` : formatCurrency(expense.bill_discount)}):</span>
                      <span className="font-medium">-{formatCurrency(expense.bill_discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Total VAT:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(expense.tax_amount || totals.tax)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
                    <span className="text-gray-900 dark:text-white">Total Amount:</span>
                    <span className="text-gray-900 dark:text-white">{formatCurrency(expense.total_amount || totals.total)}</span>
                  </div>
                </div>
              </div>

              {/* Payment Information */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Payment Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Payment Method</label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white capitalize">
                      {expense.payment_method === 'none' ? 'None' : (expense.payment_method || 'N/A')}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Payment Status</label>
                    <p className="mt-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        expense.payment_status === 'paid' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : expense.payment_status === 'partial'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {expense.payment_status === 'paid' ? 'Paid' : expense.payment_status === 'partial' ? 'Partially Paid' : 'Unpaid'}
                      </span>
                    </p>
                  </div>
                  {expense.paid_amount !== undefined && expense.paid_amount > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Paid Amount</label>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">
                        {formatCurrency(expense.paid_amount)}
                      </p>
                    </div>
                  )}
                  {expense.payment_status === 'partial' && expense.total_amount && expense.paid_amount && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 dark:text-gray-400">Outstanding Amount</label>
                      <p className="mt-1 text-sm text-red-600 font-medium">
                        {formatCurrency(expense.total_amount - expense.paid_amount)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700 gap-3">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                Close
              </button>
              {(expense.payment_status === 'unpaid' || expense.payment_status === 'partial') && (
                <button
                  onClick={() => {
                    setShowViewModal(false)
                    handleRecordPayment(expense)
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Record Payment
                </button>
              )}
              <button
                onClick={() => {
                  setShowViewModal(false)
                  handleEditExpense(expense)
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Edit Expense
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {showToast && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg ${
          toastType === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        }`}>
          {toastMessage}
        </div>
      )}

      {/* Expense View Modal */}
      {renderExpenseViewModal()}

      {/* Payment Recording Modal */}
      {showPaymentModal && expenseForPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Record Payment - {expenseForPayment.expense_number}
              </h3>
              <button
                onClick={() => {
                  setShowPaymentModal(false)
                  setExpenseForPayment(null)
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Expense Summary */}
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600 dark:text-gray-400">Total Amount:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{formatCurrency(expenseForPayment.total_amount || 0)}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600 dark:text-gray-400">Paid Amount:</span>
                  <span className="font-semibold text-gray-900 dark:text-white">{formatCurrency(expenseForPayment.paid_amount || 0)}</span>
                </div>
                <div className="flex justify-between text-sm border-t pt-2 mt-2">
                  <span className="text-gray-600 dark:text-gray-400">Outstanding:</span>
                  <span className="font-semibold text-red-600 dark:text-red-400">
                    {formatCurrency((expenseForPayment.total_amount || 0) - (expenseForPayment.paid_amount || 0))}
                  </span>
                </div>
              </div>

              {/* Payment Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Payment Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={paymentForm.payment_date}
                    onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Payment Amount <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={(expenseForPayment.total_amount || 0) - (expenseForPayment.paid_amount || 0)}
                    value={paymentForm.payment_amount}
                    onChange={(e) => setPaymentForm({...paymentForm, payment_amount: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Maximum: {formatCurrency((expenseForPayment.total_amount || 0) - (expenseForPayment.paid_amount || 0))}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Payment Method <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={paymentForm.payment_method}
                    onChange={(e) => setPaymentForm({...paymentForm, payment_method: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    required
                  >
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="check">Check</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Payment Reference
                  </label>
                  <input
                    type="text"
                    value={paymentForm.payment_reference}
                    onChange={(e) => setPaymentForm({...paymentForm, payment_reference: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    placeholder="Check number, transaction ID, etc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    placeholder="Optional notes about this payment"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700 gap-3">
              <button
                onClick={() => {
                  setShowPaymentModal(false)
                  setExpenseForPayment(null)
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePayment}
                disabled={savingPayment}
                className={`px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 ${
                  savingPayment ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {savingPayment ? 'Saving...' : 'Record Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Modal */}
      {showReportModal && reportType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold">
                {reportType === 'trial-balance' && 'Trial Balance'}
                {reportType === 'profit-loss' && 'Profit & Loss Statement'}
                {reportType === 'balance-sheet' && 'Balance Sheet'}
                {reportType === 'payables' && 'Accounts Payable'}
                {reportType === 'receivables' && 'Accounts Receivable'}
                {reportType === 'vat' && 'VAT Report'}
              </h2>
              <div className="flex items-center gap-3">
                {reportData && (
                  <button
                    onClick={() => {
                      if (reportType === 'trial-balance') {
                        exportTrialBalance()
                      } else if (reportType === 'profit-loss') {
                        exportProfitLoss()
                      } else if (reportType === 'balance-sheet') {
                        exportBalanceSheet()
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    title="Export to Excel"
                  >
                    <ArrowDownTrayIcon className="h-5 w-5" />
                    <span>Export Excel</span>
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowReportModal(false)
                    setReportType(null)
                    setReportData(null)
                  }}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto flex-1">
              {/* Report Filters */}
              <div className="mb-6 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {(reportType === 'balance-sheet' || reportType === 'payables' || reportType === 'receivables') ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        As of Date
                      </label>
                      <input
                        type="date"
                        value={reportFilters.as_of_date}
                        onChange={(e) => setReportFilters({...reportFilters, as_of_date: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                    {(reportType === 'payables' || reportType === 'receivables') && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Aging Days
                            <span className="text-xs text-gray-500 ml-1">(days to add to As of Date for aging calculation)</span>
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={reportFilters.aging_days}
                            onChange={(e) => setReportFilters({...reportFilters, aging_days: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            placeholder="0"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            {reportFilters.as_of_date && reportFilters.aging_days ? (
                              <>Aging calculated as of: {new Date(new Date(reportFilters.as_of_date).getTime() + (parseInt(reportFilters.aging_days) || 0) * 24 * 60 * 60 * 1000).toLocaleDateString()}</>
                            ) : 'Enter number of days to add to As of Date'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Filter by Aging
                          </label>
                          <select
                            value={reportFilters.aging_filter}
                            onChange={(e) => setReportFilters({...reportFilters, aging_filter: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          >
                            <option value="all">All Aging Buckets</option>
                            <option value="current">Current</option>
                            <option value="0-30">0-30 Days</option>
                            <option value="31-60">31-60 Days</option>
                            <option value="61-90">61-90 Days</option>
                            <option value="90+">90+ Days</option>
                          </select>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={reportFilters.start_date}
                        onChange={(e) => setReportFilters({...reportFilters, start_date: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={reportFilters.end_date}
                        onChange={(e) => setReportFilters({...reportFilters, end_date: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                    {reportType === 'general-ledger' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Account
                        </label>
                        <select
                          value={reportFilters.account_id}
                          onChange={(e) => setReportFilters({...reportFilters, account_id: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        >
                          <option value="">All Accounts</option>
                          {chartOfAccounts.map((account) => (
                            <option key={account.id} value={account.id}>
                              {account.account_code} - {account.account_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    {reportType === 'journal-entries' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Status
                          </label>
                          <select
                            value={reportFilters.status}
                            onChange={(e) => setReportFilters({...reportFilters, status: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          >
                            <option value="">All Status</option>
                            <option value="posted">Posted</option>
                            <option value="draft">Draft</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Source Module
                          </label>
                          <select
                            value={reportFilters.source_module}
                            onChange={(e) => setReportFilters({...reportFilters, source_module: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          >
                            <option value="">All Sources</option>
                            <option value="POS">POS</option>
                            <option value="Accounting">Accounting</option>
                            <option value="Payroll">Payroll</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Entry Number
                          </label>
                          <input
                            type="text"
                            value={reportFilters.entry_number}
                            onChange={(e) => setReportFilters({...reportFilters, entry_number: e.target.value})}
                            placeholder="Search entry number..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          />
                        </div>
                      </>
                    )}
                    {reportType === 'sales-by-category' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Order Type
                        </label>
                        <select
                          value={reportFilters.order_type}
                          onChange={(e) => setReportFilters({...reportFilters, order_type: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        >
                          <option value="">All Types</option>
                          <option value="dine-in">Dine-In</option>
                          <option value="takeaway">Takeaway</option>
                          <option value="delivery">Delivery</option>
                          <option value="catering">Catering</option>
                        </select>
                      </div>
                    )}
                    {reportType === 'expense-by-category' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Category
                          </label>
                          <select
                            value={reportFilters.category_id}
                            onChange={(e) => setReportFilters({...reportFilters, category_id: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          >
                            <option value="">All Categories</option>
                            {expenseCategories.map((cat) => (
                              <option key={cat.id} value={cat.id}>{cat.category_name}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Expense Type
                          </label>
                          <select
                            value={reportFilters.expense_type}
                            onChange={(e) => setReportFilters({...reportFilters, expense_type: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          >
                            <option value="">All Types</option>
                            <option value="direct">Direct</option>
                            <option value="indirect">Indirect</option>
                          </select>
                        </div>
                      </>
                    )}
                  </>
                )}
                <div className="flex items-end">
                  <button
                    onClick={handleLoadReport}
                    disabled={reportLoading}
                    className="w-full bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    {reportLoading ? 'Loading...' : 'Generate Report'}
                  </button>
                </div>
              </div>

              {/* Report Content */}
              {reportData && (
                <div className="space-y-6">
                  {reportType === 'trial-balance' && renderTrialBalance()}
                  {reportType === 'profit-loss' && renderProfitLoss()}
                  {reportType === 'balance-sheet' && renderBalanceSheet()}
                  {reportType === 'payables' && renderPayables()}
                  {reportType === 'receivables' && renderReceivables()}
                  {reportType === 'vat' && renderVATReport()}
                  {reportType === 'cash-flow' && renderCashFlow()}
                  {reportType === 'general-ledger' && renderGeneralLedger()}
                  {reportType === 'journal-entries' && renderJournalEntryListing()}
                  {reportType === 'sales-by-category' && renderSalesByCategory()}
                  {reportType === 'expense-by-category' && renderExpenseByCategory()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Expense Type Modal */}
      {showExpenseTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold">
                {editingExpenseType ? 'Edit Expense Type' : 'Add Expense Type'}
              </h2>
              <button
                onClick={() => setShowExpenseTypeModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category Name *
                </label>
                <input
                  type="text"
                  value={expenseTypeForm.category_name}
                  onChange={(e) => setExpenseTypeForm({...expenseTypeForm, category_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="e.g., Utilities, Rent, Marketing"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Chart of Account
                </label>
                <select
                  value={expenseTypeForm.account_id}
                  onChange={(e) => setExpenseTypeForm({...expenseTypeForm, account_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">Select Chart of Account</option>
                  {chartOfAccounts
                    .filter(account => account.account_type?.toLowerCase() === 'expense')
                    .map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.account_code} - {account.account_name}
                      </option>
                    ))}
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Link this expense type to a chart of account for automatic GL posting
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={expenseTypeForm.description}
                  onChange={(e) => setExpenseTypeForm({...expenseTypeForm, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  rows={3}
                  placeholder="Optional description for this expense type"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={expenseTypeForm.is_active}
                  onChange={(e) => setExpenseTypeForm({...expenseTypeForm, is_active: e.target.checked})}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                  Active
                </label>
              </div>
            </div>
            <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700 gap-3">
              <button
                onClick={() => setShowExpenseTypeModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveExpenseType}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                {editingExpenseType ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Accounting
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Manage your financial accounts, transactions, and reporting
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('chart')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'chart'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <CalculatorIcon className="inline-block h-5 w-5 mr-2" />
            Chart of Accounts
          </button>
          <button
            onClick={() => setActiveTab('journal')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'journal'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <DocumentTextIcon className="inline-block h-5 w-5 mr-2" />
            Journal Entries
          </button>
          <button
            onClick={() => setActiveTab('vendors')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'vendors'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <BuildingOfficeIcon className="inline-block h-5 w-5 mr-2" />
            Vendors
          </button>
          <button
            onClick={() => setActiveTab('expenses')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'expenses'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <BanknotesIcon className="inline-block h-5 w-5 mr-2" />
            Expenses
          </button>
          <button
            onClick={() => setActiveTab('expense-types')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'expense-types'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <DocumentTextIcon className="inline-block h-5 w-5 mr-2" />
            Expense Types
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'reports'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <ChartBarIcon className="inline-block h-5 w-5 mr-2" />
            Reports
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <>
            {activeTab === 'chart' && (
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold">Chart of Accounts</h2>
                  <button 
                    onClick={handleAddChartAccount}
                    className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2"
                  >
                    <CalculatorIcon className="h-5 w-5" />
                    Add Account
                  </button>
                </div>
                {/* Filters */}
                <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
                      <input
                        type="text"
                        placeholder="Search by code or name..."
                        value={chartFilters.search}
                        onChange={(e) => setChartFilters({ ...chartFilters, search: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-600"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Account Type</label>
                      <select
                        value={chartFilters.account_type}
                        onChange={(e) => setChartFilters({ ...chartFilters, account_type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-600"
                      >
                        <option value="">All Types</option>
                        <option value="asset">Asset</option>
                        <option value="liability">Liability</option>
                        <option value="equity">Equity</option>
                        <option value="revenue">Revenue</option>
                        <option value="expense">Expense</option>
                        <option value="cost of sales">Cost of Sales</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                      <select
                        value={chartFilters.is_active}
                        onChange={(e) => setChartFilters({ ...chartFilters, is_active: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-600"
                      >
                        <option value="">All</option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                    <div className="flex items-end">
                      <button
                        onClick={() => setChartFilters({ search: '', account_type: '', is_active: '' })}
                        className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500"
                      >
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Balance</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredChartOfAccounts.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                            No accounts found
                          </td>
                        </tr>
                      ) : (
                        filteredChartOfAccounts.map((account) => (
                        <tr 
                          key={account.id}
                          className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                          onClick={() => handleViewAccountDetail(account)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                            {account.account_code}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {account.account_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {account.account_type}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                            {formatCurrency(account.balance || 0)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              account.is_active 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}>
                              {account.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                            <button 
                              onClick={() => handleEditChartAccount(account)}
                              className="text-primary-600 hover:text-primary-900 dark:text-primary-400 mx-2"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                          </td>
                        </tr>
                      )))}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                {chartPagination.total_pages > 1 && (
                  <div className="mt-4 flex justify-between items-center px-6 pb-6">
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Page {chartPagination.page} of {chartPagination.total_pages} ({chartPagination.total} entries)
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const newPage = chartPagination.page - 1
                          setChartPagination({ ...chartPagination, page: newPage })
                          loadData(newPage)
                        }}
                        disabled={chartPagination.page === 1}
                        className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                        Previous
                      </button>
                      <button
                        onClick={() => {
                          const newPage = chartPagination.page + 1
                          setChartPagination({ ...chartPagination, page: newPage })
                          loadData(newPage)
                        }}
                        disabled={chartPagination.page >= chartPagination.total_pages}
                        className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        Next
                        <ChevronRightIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'journal' && (
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold">Journal Entries</h2>
                  <button 
                    onClick={handleAddJournalEntry}
                    className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2"
                  >
                    <DocumentTextIcon className="h-5 w-5" />
                    Add Entry
                  </button>
                </div>
                {/* Filters */}
                <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
                      <input
                        type="text"
                        placeholder="Search entry # or description..."
                        value={journalFilters.search}
                        onChange={(e) => setJournalFilters({ ...journalFilters, search: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-600"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={journalFilters.start_date}
                        onChange={(e) => setJournalFilters({ ...journalFilters, start_date: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-600"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
                      <input
                        type="date"
                        value={journalFilters.end_date}
                        onChange={(e) => setJournalFilters({ ...journalFilters, end_date: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-600"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                      <select
                        value={journalFilters.status}
                        onChange={(e) => setJournalFilters({ ...journalFilters, status: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-600"
                      >
                        <option value="">All</option>
                        <option value="posted">Posted</option>
                        <option value="draft">Draft</option>
                      </select>
                    </div>
                    <div className="flex items-end">
                      <button
                        onClick={() => setJournalFilters({ search: '', start_date: '', end_date: '', status: '' })}
                        className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500"
                      >
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entry #</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Debit</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Credit</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredJournalEntries.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                            No journal entries found
                          </td>
                        </tr>
                      ) : (
                        filteredJournalEntries.map((entry) => (
                          <tr key={entry.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                              {entry.entry_number}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {new Date(entry.entry_date).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                              {entry.description}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {formatCurrency(entry.total_debit)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {formatCurrency(entry.total_credit)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                entry.status === 'posted' 
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                                  : 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                              }`}>
                                {entry.status === 'posted' ? 'Posted' : 'Draft'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => handleViewJournalEntry(entry.id)}
                                  className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
                                  title="View"
                                >
                                  <EyeIcon className="h-5 w-5" />
                                </button>
                                {isSuperAdmin && (
                                  <button
                                    onClick={() => handleDeleteJournalEntry(entry.id)}
                                    className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                    title="Delete"
                                  >
                                    <TrashIcon className="h-5 w-5" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                {journalPagination.total_pages > 1 && (
                  <div className="mt-4 flex justify-between items-center px-6 pb-6">
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Page {journalPagination.page} of {journalPagination.total_pages} ({journalPagination.total} entries)
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const newPage = journalPagination.page - 1
                          setJournalPagination({ ...journalPagination, page: newPage })
                          loadData(newPage)
                        }}
                        disabled={journalPagination.page === 1}
                        className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                        Previous
                      </button>
                      <button
                        onClick={() => {
                          const newPage = journalPagination.page + 1
                          setJournalPagination({ ...journalPagination, page: newPage })
                          loadData(newPage)
                        }}
                        disabled={journalPagination.page >= journalPagination.total_pages}
                        className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        Next
                        <ChevronRightIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'vendors' && (
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold">Vendors</h2>
                  <button 
                    onClick={handleAddVendor}
                    className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2"
                  >
                    <BuildingOfficeIcon className="h-5 w-5" />
                    Add Vendor
                  </button>
                </div>
                {/* Filters */}
                <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Vendor</label>
                      <select
                        value={vendorFilters.vendor_id}
                        onChange={(e) => setVendorFilters({ ...vendorFilters, vendor_id: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-600"
                      >
                        <option value="">All Vendors</option>
                        {vendors.map((vendor) => (
                          <option key={vendor.id} value={vendor.id}>
                            {vendor.name || vendor.vendor_name || 'Unnamed Vendor'}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
                      <input
                        type="text"
                        placeholder="Search by name, contact, email, phone..."
                        value={vendorFilters.search}
                        onChange={(e) => setVendorFilters({ ...vendorFilters, search: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-600"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">City</label>
                      <input
                        type="text"
                        placeholder="Filter by city..."
                        value={vendorFilters.city}
                        onChange={(e) => setVendorFilters({ ...vendorFilters, city: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-600"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        onClick={() => setVendorFilters({ search: '', city: '', vendor_id: '' })}
                        className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500"
                      >
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">City</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredVendors.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                            No vendors found
                          </td>
                        </tr>
                      ) : (
                        filteredVendors.map((vendor) => (
                          <tr key={vendor.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                              {vendor.name || vendor.vendor_name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {vendor.contact_person}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {vendor.email}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {vendor.phone}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {vendor.city}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex justify-end gap-2">
                                <button 
                                  onClick={() => handleEditVendor(vendor)}
                                  className="text-primary-600 hover:text-primary-900 dark:text-primary-400"
                                >
                                  <PencilIcon className="h-5 w-5" />
                                </button>
                                <button 
                                  onClick={() => handleDeleteVendor(vendor)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                {vendorPagination.total_pages > 1 && (
                  <div className="mt-4 flex justify-between items-center px-6 pb-6">
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Page {vendorPagination.page} of {vendorPagination.total_pages} ({vendorPagination.total} entries)
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const newPage = vendorPagination.page - 1
                          setVendorPagination({ ...vendorPagination, page: newPage })
                          loadData(newPage)
                        }}
                        disabled={vendorPagination.page === 1}
                        className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                        Previous
                      </button>
                      <button
                        onClick={() => {
                          const newPage = vendorPagination.page + 1
                          setVendorPagination({ ...vendorPagination, page: newPage })
                          loadData(newPage)
                        }}
                        disabled={vendorPagination.page >= vendorPagination.total_pages}
                        className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        Next
                        <ChevronRightIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'expense-types' && (
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold">Expense Types</h2>
                  <button 
                    onClick={handleAddExpenseType}
                    className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2"
                  >
                    <DocumentTextIcon className="h-5 w-5" />
                    Add Expense Type
                  </button>
                </div>
                {/* Filters */}
                <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
                      <input
                        type="text"
                        placeholder="Search by name or description..."
                        value={expenseTypeFilters.search}
                        onChange={(e) => setExpenseTypeFilters({ ...expenseTypeFilters, search: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-600"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                      <select
                        value={expenseTypeFilters.is_active}
                        onChange={(e) => setExpenseTypeFilters({ ...expenseTypeFilters, is_active: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-600"
                      >
                        <option value="">All</option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                    <div className="flex items-end">
                      <button
                        onClick={() => setExpenseTypeFilters({ search: '', is_active: '' })}
                        className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500"
                      >
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Chart of Account</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredExpenseCategories.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                            No expense types found
                          </td>
                        </tr>
                      ) : (
                        filteredExpenseCategories.map((category) => (
                          <tr key={category.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                              {category.category_name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {category.chart_of_accounts ? (
                                <span>{category.chart_of_accounts.account_code} - {category.chart_of_accounts.account_name}</span>
                              ) : (
                                <span className="text-gray-400 italic">Not linked</span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                              {category.description || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                category.is_active 
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              }`}>
                                {category.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex justify-end gap-2">
                                <button 
                                  onClick={() => handleEditExpenseType(category)}
                                  className="text-primary-600 hover:text-primary-900 dark:text-primary-400"
                                >
                                  <PencilIcon className="h-5 w-5" />
                                </button>
                                <button 
                                  onClick={() => handleDeleteExpenseType(category.id)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                {expenseTypePagination.total_pages > 1 && (
                  <div className="mt-4 flex justify-between items-center px-6 pb-6">
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Page {expenseTypePagination.page} of {expenseTypePagination.total_pages} ({expenseTypePagination.total} entries)
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const newPage = expenseTypePagination.page - 1
                          setExpenseTypePagination({ ...expenseTypePagination, page: newPage })
                          loadData(newPage)
                        }}
                        disabled={expenseTypePagination.page === 1}
                        className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                        Previous
                      </button>
                      <button
                        onClick={() => {
                          const newPage = expenseTypePagination.page + 1
                          setExpenseTypePagination({ ...expenseTypePagination, page: newPage })
                          loadData(newPage)
                        }}
                        disabled={expenseTypePagination.page >= expenseTypePagination.total_pages}
                        className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        Next
                        <ChevronRightIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'expenses' && (
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold">Expenses</h2>
                  <button 
                    onClick={handleAddExpense}
                    className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2"
                  >
                    <BanknotesIcon className="h-5 w-5" />
                    Add Expense
                  </button>
                </div>
                
                {/* Analytics Cards */}
                {expenseAnalytics && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    {/* Total Expenses */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Expenses</p>
                          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                            {formatCurrency(expenseAnalytics.total_expenses || 0)}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {expenseAnalytics.total_count || 0} expenses
                          </p>
                        </div>
                        <div className="bg-blue-100 dark:bg-blue-900/20 rounded-full p-3">
                          <BanknotesIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                        </div>
                      </div>
                    </div>

                    {/* Outstanding Payables */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Outstanding Payables</p>
                          <p className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">
                            {formatCurrency(expenseAnalytics.outstanding_payables || 0)}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {expenseAnalytics.unpaid_count || 0} unpaid / {expenseAnalytics.partial_count || 0} partial
                          </p>
                        </div>
                        <div className="bg-red-100 dark:bg-red-900/20 rounded-full p-3">
                          <ChartBarIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                        </div>
                      </div>
                    </div>

                    {/* Paid Expenses */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Paid Expenses</p>
                          <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                            {formatCurrency(expenseAnalytics.paid_expenses || 0)}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {expenseAnalytics.paid_count || 0} expenses
                          </p>
                        </div>
                        <div className="bg-green-100 dark:bg-green-900/20 rounded-full p-3">
                          <BanknotesIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                        </div>
                      </div>
                    </div>

                    {/* Average Expense */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Average Expense</p>
                          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                            {formatCurrency(expenseAnalytics.average_expense || 0)}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Per expense
                          </p>
                        </div>
                        <div className="bg-purple-100 dark:bg-purple-900/20 rounded-full p-3">
                          <ChartBarIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Aging Expenses Breakdown */}
                {expenseAnalytics && expenseAnalytics.aging_breakdown && (
                  <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Aging Expenses</h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Current</p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(expenseAnalytics.aging_breakdown.current || 0)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {expenseAnalytics.aging_breakdown.current_count || 0} expenses
                        </p>
                      </div>
                      <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">0-30 Days</p>
                        <p className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                          {formatCurrency(expenseAnalytics.aging_breakdown['0-30'] || 0)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {expenseAnalytics.aging_breakdown['0-30_count'] || 0} expenses
                        </p>
                      </div>
                      <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">31-60 Days</p>
                        <p className="text-lg font-semibold text-yellow-600 dark:text-yellow-400">
                          {formatCurrency(expenseAnalytics.aging_breakdown['31-60'] || 0)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {expenseAnalytics.aging_breakdown['31-60_count'] || 0} expenses
                        </p>
                      </div>
                      <div className="text-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">61-90 Days</p>
                        <p className="text-lg font-semibold text-orange-600 dark:text-orange-400">
                          {formatCurrency(expenseAnalytics.aging_breakdown['61-90'] || 0)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {expenseAnalytics.aging_breakdown['61-90_count'] || 0} expenses
                        </p>
                      </div>
                      <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">90+ Days</p>
                        <p className="text-lg font-semibold text-red-600 dark:text-red-400">
                          {formatCurrency(expenseAnalytics.aging_breakdown['90+'] || 0)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {expenseAnalytics.aging_breakdown['90+_count'] || 0} expenses
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Expenses by Category */}
                {expenseAnalytics && expenseAnalytics.by_category && expenseAnalytics.by_category.length > 0 && (
                  <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Expenses by Category</h3>
                    <div className="space-y-3">
                      {expenseAnalytics.by_category.slice(0, 5).map((cat: any, index: number) => (
                        <div key={index} className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {cat.category_name || 'Uncategorized'}
                              </span>
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {formatCurrency(cat.total_amount)}
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                              <div
                                className="bg-primary-600 h-2 rounded-full"
                                style={{ width: `${(cat.total_amount / (expenseAnalytics.total_expenses || 1)) * 100}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Filters */}
                <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <div className="lg:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
                      <input
                        type="text"
                        placeholder="Search by number, description, payee..."
                        value={expenseFilters.search}
                        onChange={(e) => setExpenseFilters({ ...expenseFilters, search: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-600"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                      <select
                        value={expenseFilters.category_id}
                        onChange={(e) => setExpenseFilters({ ...expenseFilters, category_id: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-600"
                      >
                        <option value="">All Categories</option>
                        {expenseCategories.map((cat) => (
                          <option key={cat.id} value={cat.id}>{cat.category_name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Vendor</label>
                      <select
                        value={expenseFilters.vendor_id}
                        onChange={(e) => setExpenseFilters({ ...expenseFilters, vendor_id: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-600"
                      >
                        <option value="">All Vendors</option>
                        {vendors.map((vendor) => (
                          <option key={vendor.id} value={vendor.id}>{vendor.name || vendor.vendor_name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Payment Status</label>
                      <select
                        value={expenseFilters.payment_status}
                        onChange={(e) => setExpenseFilters({ ...expenseFilters, payment_status: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-600"
                      >
                        <option value="">All</option>
                        <option value="paid">Paid</option>
                        <option value="unpaid">Unpaid</option>
                        <option value="partial">Partial</option>
                      </select>
                    </div>
                    <div className="flex items-end">
                      <button
                        onClick={() => setExpenseFilters({ search: '', category_id: '', vendor_id: '', payment_status: '', start_date: '', end_date: '' })}
                        className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={expenseFilters.start_date}
                        onChange={(e) => setExpenseFilters({ ...expenseFilters, start_date: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-600"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
                      <input
                        type="date"
                        value={expenseFilters.end_date}
                        onChange={(e) => setExpenseFilters({ ...expenseFilters, end_date: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-800 dark:border-gray-600"
                      />
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredExpenses.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                            No expenses found
                          </td>
                        </tr>
                      ) : (
                        filteredExpenses.map((expense) => (
                          <tr key={expense.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {new Date(expense.expense_date).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                              {expense.description}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {(() => {
                                const category = expenseCategories.find(cat => cat.id === expense.category_id)
                                return category ? category.category_name : 'N/A'
                              })()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                              {formatCurrency(expense.total_amount || expense.amount || 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {expense.payment_method}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex justify-end gap-2">
                                <button 
                                  onClick={() => handleViewExpense(expense)}
                                  className="text-blue-600 hover:text-blue-900 dark:text-blue-400"
                                  title="View Details"
                                >
                                  <EyeIcon className="h-5 w-5" />
                                </button>
                                {(expense.payment_status === 'unpaid' || expense.payment_status === 'partial') && (
                                  <button 
                                    onClick={() => handleRecordPayment(expense)}
                                    className="text-green-600 hover:text-green-900 dark:text-green-400"
                                    title="Record Payment"
                                  >
                                    <BanknotesIcon className="h-5 w-5" />
                                  </button>
                                )}
                                <button 
                                  onClick={() => handleEditExpense(expense)}
                                  className="text-primary-600 hover:text-primary-900 dark:text-primary-400"
                                  title="Edit"
                                >
                                  <PencilIcon className="h-5 w-5" />
                                </button>
                                <button 
                                  onClick={() => handleDeleteExpense(expense)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete"
                                >
                                  <TrashIcon className="h-5 w-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                {expensePagination.total_pages > 1 && (
                  <div className="mt-4 flex justify-between items-center px-6 pb-6">
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Page {expensePagination.page} of {expensePagination.total_pages} ({expensePagination.total} entries)
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const newPage = expensePagination.page - 1
                          setExpensePagination({ ...expensePagination, page: newPage })
                          loadData(newPage)
                        }}
                        disabled={expensePagination.page === 1}
                        className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                        Previous
                      </button>
                      <button
                        onClick={() => {
                          const newPage = expensePagination.page + 1
                          setExpensePagination({ ...expensePagination, page: newPage })
                          loadData(newPage)
                        }}
                        disabled={expensePagination.page >= expensePagination.total_pages}
                        className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        Next
                        <ChevronRightIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'reports' && (
              <div className="p-6">
                <h2 className="text-xl font-semibold mb-6">Financial Reports</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold mb-2">Trial Balance</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                      View account balances and totals
                    </p>
                    <button 
                      onClick={() => handleViewReport('trial-balance')}
                      className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 w-full"
                    >
                      View Report
                    </button>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold mb-2">Profit & Loss</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                      Income and expense summary
                    </p>
                    <button 
                      onClick={() => handleViewReport('profit-loss')}
                      className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 w-full"
                    >
                      View Report
                    </button>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold mb-2">Balance Sheet</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                      Assets, liabilities, and equity
                    </p>
                    <button 
                      onClick={() => handleViewReport('balance-sheet')}
                      className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 w-full"
                    >
                      View Report
                    </button>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold mb-2">Accounts Payable</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                      Outstanding payables with aging analysis
                    </p>
                    <button 
                      onClick={() => handleViewReport('payables')}
                      className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 w-full"
                    >
                      View Report
                    </button>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold mb-2">Accounts Receivable</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                      Outstanding receivables with aging analysis
                    </p>
                    <button 
                      onClick={() => handleViewReport('receivables')}
                      className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 w-full"
                    >
                      View Report
                    </button>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold mb-2">VAT Report</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                      Detailed VAT input, output, and control report
                    </p>
                    <button 
                      onClick={() => handleViewReport('vat')}
                      className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 w-full"
                    >
                      View Report
                    </button>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold mb-2">Cash Flow Statement</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                      Cash inflows and outflows by activity
                    </p>
                    <button 
                      onClick={() => handleViewReport('cash-flow')}
                      className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 w-full"
                    >
                      View Report
                    </button>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold mb-2">General Ledger</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                      Complete ledger for all accounts
                    </p>
                    <button 
                      onClick={() => handleViewReport('general-ledger')}
                      className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 w-full"
                    >
                      View Report
                    </button>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold mb-2">Journal Entry Listing</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                      List all journal entries with details
                    </p>
                    <button 
                      onClick={() => handleViewReport('journal-entries')}
                      className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 w-full"
                    >
                      View Report
                    </button>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold mb-2">Sales by Category</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                      Revenue breakdown by product category
                    </p>
                    <button 
                      onClick={() => handleViewReport('sales-by-category')}
                      className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 w-full"
                    >
                      View Report
                    </button>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold mb-2">Expense by Category</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                      Expense breakdown by category and type
                    </p>
                    <button 
                      onClick={() => handleViewReport('expense-by-category')}
                      className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 w-full"
                    >
                      View Report
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {renderModal()}

      {/* Account Detail Modal */}
      {showAccountDetailModal && selectedAccount && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowAccountDetailModal(false)}></div>
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Account Details: {selectedAccount.account_code} - {selectedAccount.account_name}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Type: {selectedAccount.account_type} | Status: {selectedAccount.is_active ? 'Active' : 'Inactive'}
                  </p>
                </div>
                <button 
                  onClick={() => setShowAccountDetailModal(false)} 
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                {/* Account Summary */}
                {ledgerSummary && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Opening Balance</p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        {formatCurrency(ledgerSummary.opening_balance || 0)}
                      </p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Total Debit</p>
                      <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                        {formatCurrency(ledgerSummary.total_debit || 0)}
                      </p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Total Credit</p>
                      <p className="text-lg font-semibold text-red-600 dark:text-red-400">
                        {formatCurrency(ledgerSummary.total_credit || 0)}
                      </p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Closing Balance</p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        {formatCurrency(ledgerSummary.closing_balance || 0)}
                      </p>
                    </div>
                  </div>
                )}

                {/* Filters */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={ledgerFilters.start_date}
                      onChange={(e) => {
                        const newFilters = { ...ledgerFilters, start_date: e.target.value, page: 1 }
                        setLedgerFilters(newFilters)
                        if (selectedAccount) {
                          setTimeout(() => loadAccountLedger(selectedAccount.id, newFilters), 100)
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={ledgerFilters.end_date}
                      onChange={(e) => {
                        const newFilters = { ...ledgerFilters, end_date: e.target.value, page: 1 }
                        setLedgerFilters(newFilters)
                        if (selectedAccount) {
                          setTimeout(() => loadAccountLedger(selectedAccount.id, newFilters), 100)
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <button
                      onClick={() => {
                        const newFilters = { start_date: '', end_date: '', page: 1, limit: 100 }
                        setLedgerFilters(newFilters)
                        if (selectedAccount) {
                          setTimeout(() => loadAccountLedger(selectedAccount.id, newFilters), 100)
                        }
                      }}
                      className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
                    >
                      Clear Filters
                    </button>
                    <button
                      onClick={() => handleExportLedger('csv')}
                      className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700"
                    >
                      Export CSV
                    </button>
                  </div>
                </div>

                {/* Ledger Table */}
                {ledgerLoading ? (
                  <div className="text-center py-8">Loading ledger...</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entry #</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Debit</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Credit</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {accountLedger.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                              No ledger entries found
                            </td>
                          </tr>
                        ) : (
                          accountLedger.map((entry) => (
                            <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {entry.entry_date || new Date(entry.created_at).toLocaleDateString()}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {entry.source_transaction_id && entry.source_module ? (
                                  <span 
                                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline cursor-pointer font-medium"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleViewSourceTransaction(entry)
                                    }}
                                    title="Click to view transaction details"
                                  >
                                    {entry.entry_number || '-'}
                                  </span>
                                ) : (
                                  entry.entry_number || '-'
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                                {entry.source_transaction_id && entry.source_module ? (
                                  <span 
                                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline cursor-pointer font-medium"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleViewSourceTransaction(entry)
                                    }}
                                    title="Click to view transaction details"
                                  >
                                    {entry.description || entry.entry_description || '-'}
                                    <svg className="inline-block ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                  </span>
                                ) : (
                                  entry.description || entry.entry_description || '-'
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {entry.source_module || '-'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-green-600 dark:text-green-400">
                                {entry.debit > 0 ? formatCurrency(entry.debit) : '-'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-red-600 dark:text-red-400">
                                {entry.credit > 0 ? formatCurrency(entry.credit) : '-'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-gray-900 dark:text-white">
                                {formatCurrency(entry.running_balance)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                {ledgerPagination && ledgerPagination.totalPages > 1 && (
                  <div className="mt-4 flex justify-between items-center">
                    <div className="text-sm text-gray-500">
                      Page {ledgerPagination.page} of {ledgerPagination.totalPages} ({ledgerPagination.total} entries)
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          if (ledgerPagination.page > 1) {
                            const newFilters = { ...ledgerFilters, page: ledgerPagination.page - 1 }
                            setLedgerFilters(newFilters)
                            if (selectedAccount) {
                              setTimeout(() => loadAccountLedger(selectedAccount.id, newFilters), 100)
                            }
                          }
                        }}
                        disabled={ledgerPagination.page === 1}
                        className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => {
                          if (ledgerPagination.page < ledgerPagination.totalPages) {
                            const newFilters = { ...ledgerFilters, page: ledgerPagination.page + 1 }
                            setLedgerFilters(newFilters)
                            if (selectedAccount) {
                              setTimeout(() => loadAccountLedger(selectedAccount.id, newFilters), 100)
                            }
                          }
                        }}
                        disabled={ledgerPagination.page === ledgerPagination.totalPages}
                        className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Source Transaction Modal */}
      {showSourceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {sourceLoading ? 'Loading...' : sourceTransaction?.type === 'expense' ? 'Expense Details' : sourceTransaction?.type === 'order' ? 'Order Details' : sourceTransaction?.type === 'payroll' ? 'Payroll Details' : 'Transaction Details'}
                </h3>
                <button
                  onClick={() => {
                    setShowSourceModal(false)
                    setSourceTransaction(null)
                  }}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6">
              {sourceLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
                  <p className="mt-2 text-gray-500 dark:text-gray-400">Loading transaction details...</p>
                </div>
              ) : sourceTransaction?.type === 'expense' ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Expense Number</label>
                      <p className="text-sm text-gray-900 dark:text-white">{sourceTransaction.data.expense_number || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Date</label>
                      <p className="text-sm text-gray-900 dark:text-white">{new Date(sourceTransaction.data.expense_date).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Vendor/Payee</label>
                      <p className="text-sm text-gray-900 dark:text-white">{sourceTransaction.data.payee_name || sourceTransaction.data.vendor_name || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Payment Status</label>
                      <p className="text-sm text-gray-900 dark:text-white capitalize">{sourceTransaction.data.payment_status || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Amount</label>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">{formatCurrency(sourceTransaction.data.total_amount || 0)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Payment Method</label>
                      <p className="text-sm text-gray-900 dark:text-white capitalize">{sourceTransaction.data.payment_method || '-'}</p>
                    </div>
                  </div>
                  {sourceTransaction.data.description && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Description</label>
                      <p className="text-sm text-gray-900 dark:text-white">{sourceTransaction.data.description}</p>
                    </div>
                  )}
                  {sourceTransaction.data.expense_items && sourceTransaction.data.expense_items.length > 0 && (
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 block">Items</label>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Item</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Quantity</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Unit Price</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Total</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {sourceTransaction.data.expense_items.map((item: any, idx: number) => (
                              <tr key={idx}>
                                <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{item.item_name}</td>
                                <td className="px-4 py-2 text-sm text-right text-gray-900 dark:text-white">{item.quantity || 1}</td>
                                <td className="px-4 py-2 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(item.unit_price || 0)}</td>
                                <td className="px-4 py-2 text-sm text-right text-gray-900 dark:text-white">{formatCurrency(item.total_amount || 0)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ) : sourceTransaction?.type === 'order' ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Order Number</label>
                      <p className="text-sm text-gray-900 dark:text-white">{sourceTransaction.data.order_number || sourceTransaction.data.id || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Date</label>
                      <p className="text-sm text-gray-900 dark:text-white">{new Date(sourceTransaction.data.created_at).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Table</label>
                      <p className="text-sm text-gray-900 dark:text-white">{sourceTransaction.data.table_number || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Order Type</label>
                      <p className="text-sm text-gray-900 dark:text-white capitalize">{sourceTransaction.data.order_type || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Amount</label>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">{formatCurrency(sourceTransaction.data.total_amount || 0)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Payment Status</label>
                      <p className="text-sm text-gray-900 dark:text-white capitalize">{sourceTransaction.data.payment_status || '-'}</p>
                    </div>
                  </div>
                </div>
              ) : sourceTransaction?.type === 'payroll' ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Employee</label>
                      <p className="text-sm text-gray-900 dark:text-white">{sourceTransaction.data.employee_name || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Period</label>
                      <p className="text-sm text-gray-900 dark:text-white">{sourceTransaction.data.pay_period || '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Gross Salary</label>
                      <p className="text-sm text-gray-900 dark:text-white">{formatCurrency(sourceTransaction.data.gross_salary || 0)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Net Salary</label>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">{formatCurrency(sourceTransaction.data.net_salary || 0)}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No transaction details available
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Expense Confirmation Modal */}
      {showDeleteExpenseModal && expenseToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Delete Expense
            </h3>
            
            <div className="mb-6">
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                Are you sure you want to delete this expense?
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <p className="font-medium text-gray-900 dark:text-white">
                  {expenseToDelete.expense_number || 'Expense'}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {expenseToDelete.payee_name || expenseToDelete.description || 'No description'}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Amount: {formatCurrency(expenseToDelete.total_amount || expenseToDelete.amount || 0)}
                </p>
                {expenseToDelete.expense_date && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Date: {new Date(expenseToDelete.expense_date).toLocaleDateString()}
                  </p>
                )}
              </div>
              <p className="text-sm text-red-600 dark:text-red-400 mt-3">
                This action cannot be undone.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteExpenseModal(false)
                  setExpenseToDelete(null)
                }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteExpense}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Delete Expense
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Vendor Confirmation Modal */}
      {showDeleteVendorModal && vendorToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Delete Vendor
            </h3>
            
            <div className="mb-6">
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                Are you sure you want to delete this vendor?
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <p className="font-medium text-gray-900 dark:text-white">
                  {vendorToDelete.vendor_name || vendorToDelete.name}
                </p>
                {vendorToDelete.contact_person && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Contact: {vendorToDelete.contact_person}
                  </p>
                )}
                {vendorToDelete.email && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Email: {vendorToDelete.email}
                  </p>
                )}
                {vendorToDelete.phone && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Phone: {vendorToDelete.phone}
                  </p>
                )}
              </div>
              <p className="text-sm text-red-600 dark:text-red-400 mt-3">
                This action cannot be undone.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteVendorModal(false)
                  setVendorToDelete(null)
                }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteVendor}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Delete Vendor
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Journal Entry Detail Modal */}
      {showJournalDetailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Journal Entry Details</h2>
              <div className="flex items-center gap-2">
                {!journalDetailLoading && viewingJournalEntry?.status === 'draft' && (
                  <button
                    onClick={handleApproveJournalEntry}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-medium"
                  >
                    <CheckCircleIcon className="h-5 w-5" />
                    Approve (Post)
                  </button>
                )}
                {isSuperAdmin && viewingJournalEntry && (
                  <button
                    onClick={() => handleDeleteJournalEntry(viewingJournalEntry.id)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium"
                    title="Delete journal entry"
                  >
                    <TrashIcon className="h-5 w-5" />
                    Delete
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowJournalDetailModal(false)
                    setViewingJournalEntry(null)
                  }}
                  className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              {journalDetailLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
                  <p className="mt-2 text-gray-500 dark:text-gray-400">Loading journal entry...</p>
                </div>
              ) : viewingJournalEntry ? (
                <div className="space-y-6">
                  {/* Header Information */}
                  <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Entry Number</label>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">{viewingJournalEntry.entry_number}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Date</label>
                        <p className="text-lg text-gray-900 dark:text-white">{new Date(viewingJournalEntry.entry_date).toLocaleDateString()}</p>
                      </div>
                      <div className="col-span-2">
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Description</label>
                        <p className="text-sm text-gray-900 dark:text-white">{viewingJournalEntry.description}</p>
                      </div>
                      {viewingJournalEntry.reference_type && (
                        <div>
                          <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Reference Type</label>
                          <p className="text-sm text-gray-900 dark:text-white">{viewingJournalEntry.reference_type.replace('_', ' ')}</p>
                        </div>
                      )}
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</label>
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          viewingJournalEntry.status === 'posted' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                            : 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                        }`}>
                          {viewingJournalEntry.status === 'posted' ? 'Posted' : 'Draft'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Journal Entry Lines */}
                  {viewingJournalEntry.journal_entry_lines && viewingJournalEntry.journal_entry_lines.length > 0 ? (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Journal Entry Lines</h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Account</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Debit</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Credit</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {viewingJournalEntry.journal_entry_lines
                              .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                              .map((line) => (
                              <tr key={line.id}>
                                <td className="px-4 py-3">
                                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                                    {line.chart_of_accounts ? (
                                      <>
                                        {line.chart_of_accounts.account_code} - {line.chart_of_accounts.account_name}
                                      </>
                                    ) : (
                                      'Unknown Account'
                                    )}
                                  </div>
                                  {line.description && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{line.description}</div>
                                  )}
                                  {line.narration && (
                                    <div className="text-xs text-gray-400 dark:text-gray-500 mt-1 italic">{line.narration}</div>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-right text-sm text-gray-900 dark:text-white">
                                  {line.debit_amount > 0 ? formatCurrency(line.debit_amount) : '-'}
                                </td>
                                <td className="px-4 py-3 text-right text-sm text-gray-900 dark:text-white">
                                  {line.credit_amount > 0 ? formatCurrency(line.credit_amount) : '-'}
                                </td>
                              </tr>
                            ))}
                            <tr className="bg-gray-50 dark:bg-gray-700 font-semibold">
                              <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">Total</td>
                              <td className="px-4 py-3 text-right text-sm text-gray-900 dark:text-white">
                                {formatCurrency(viewingJournalEntry.total_debit)}
                              </td>
                              <td className="px-4 py-3 text-right text-sm text-gray-900 dark:text-white">
                                {formatCurrency(viewingJournalEntry.total_credit)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      No journal entry lines found
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No journal entry data available
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Account Details Modal */}
      {showAccountDetailsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Account Details: {selectedAccountForDetails?.account_code} - {selectedAccountForDetails?.account_name}
                </h2>
                {reportType === 'balance-sheet' && reportFilters.as_of_date ? (
                  <p className="text-sm text-gray-500 mt-1">
                    As of: {new Date(reportFilters.as_of_date).toLocaleDateString()}
                  </p>
                ) : (reportFilters.start_date || reportFilters.end_date) ? (
                  <p className="text-sm text-gray-500 mt-1">
                    {reportFilters.start_date && `From: ${new Date(reportFilters.start_date).toLocaleDateString()}`}
                    {reportFilters.start_date && reportFilters.end_date && ' - '}
                    {reportFilters.end_date && `To: ${new Date(reportFilters.end_date).toLocaleDateString()}`}
                  </p>
                ) : null}
              </div>
              <button
                onClick={() => {
                  setShowAccountDetailsModal(false)
                  setSelectedAccountForDetails(null)
                  setAccountDetails(null)
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6">
              {loadingAccountDetails ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="mt-4 text-gray-500">Loading transactions...</p>
                </div>
              ) : accountDetails && accountDetails.ledger && accountDetails.ledger.length > 0 ? (
                <div>
                  <div className="mb-4 flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-500">
                        Total Transactions: {accountDetails.ledger.length}
                      </p>
                      {accountDetails.summary && (
                        <p className="text-sm text-gray-500 mt-1">
                          Opening Balance: {formatCurrency(accountDetails.summary.opening_balance || 0)} | 
                          Total Debit: {formatCurrency(accountDetails.summary.total_debit || 0)} | 
                          Total Credit: {formatCurrency(accountDetails.summary.total_credit || 0)} | 
                          Closing Balance: {formatCurrency(accountDetails.summary.closing_balance || 0)}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Entry Number</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Description</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Debit</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Credit</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Balance</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {accountDetails.ledger.map((entry: LedgerEntry, idx: number) => (
                          <tr 
                            key={entry.id || idx} 
                            className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                            onClick={() => handleTransactionClick(entry)}
                          >
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                              {entry.entry_date || entry.journal_entry?.entry_date 
                                ? new Date(entry.entry_date || (entry.journal_entry?.entry_date || '')).toLocaleDateString()
                                : 'N/A'}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className="text-blue-600 dark:text-blue-400 hover:underline font-medium">
                                {entry.entry_number || entry.journal_entry?.entry_number || 'N/A'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                              {entry.description || entry.journal_entry?.description || 'N/A'}
                              {entry.journal_entry?.source_transaction_id && (
                                <div className="text-xs mt-1">
                                  <span className="text-gray-500">Source: </span>
                                  <span className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer">
                                    {entry.journal_entry.source_module === 'POS' ? `Order #${entry.journal_entry.source_transaction_id}` : entry.journal_entry.source_transaction_id}
                                  </span>
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">
                              {(entry.debit || 0) > 0 ? formatCurrency(entry.debit || 0) : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-white">
                              {(entry.credit || 0) > 0 ? formatCurrency(entry.credit || 0) : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 dark:text-white">
                              {formatCurrency(entry.running_balance || 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p className="text-lg mb-2">No transactions found</p>
                  <p className="text-sm">
                    {(reportType === 'balance-sheet' && reportFilters.as_of_date) || reportFilters.start_date || reportFilters.end_date
                      ? 'No transactions match the selected date range.'
                      : 'This account has no transactions yet.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Transaction/Journal Entry Detail Modal */}
      {showTransactionDetailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Journal Entry Details
                </h2>
                {selectedTransaction?.journal_entry?.entry_number && (
                  <p className="text-sm text-gray-500 mt-1">
                    Entry Number: <span className="text-blue-600 dark:text-blue-400 font-medium">{selectedTransaction.journal_entry.entry_number}</span>
                  </p>
                )}
              </div>
              <button
                onClick={() => {
                  setShowTransactionDetailModal(false)
                  setSelectedTransaction(null)
                  setTransactionDetail(null)
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6">
              {loadingTransactionDetail ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="mt-4 text-gray-500">Loading transaction details...</p>
                </div>
              ) : transactionDetail ? (
                <div className="space-y-6">
                  {/* Journal Entry Header Info */}
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Entry Number</label>
                        <p className="text-sm font-semibold text-blue-600 dark:text-blue-400 mt-1">
                          {transactionDetail.entry_number || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</label>
                        <p className="text-sm text-gray-900 dark:text-white mt-1">
                          {transactionDetail.entry_date 
                            ? new Date(transactionDetail.entry_date).toLocaleDateString()
                            : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</label>
                        <span className={`mt-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          transactionDetail.status === 'posted' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                        }`}>
                          {transactionDetail.status || 'N/A'}
                        </span>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Source Module</label>
                        <p className="text-sm text-gray-900 dark:text-white mt-1">
                          {transactionDetail.source_module || 'N/A'}
                        </p>
                      </div>
                    </div>
                    {transactionDetail.description && (
                      <div className="mt-4">
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Description</label>
                        <p className="text-sm text-gray-900 dark:text-white mt-1">{transactionDetail.description}</p>
                      </div>
                    )}
                    {transactionDetail.source_transaction_id && (
                      <div className="mt-4">
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Source Transaction</label>
                        <p className="text-sm mt-1">
                          <span className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer font-medium">
                            {transactionDetail.source_module === 'POS' ? `Order #${transactionDetail.source_transaction_id}` : transactionDetail.source_transaction_id}
                          </span>
                        </p>
                      </div>
                    )}
                    {transactionDetail.reference_type && (
                      <div className="mt-4">
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Reference Type</label>
                        <p className="text-sm text-gray-900 dark:text-white mt-1">
                          {transactionDetail.reference_type.replace('_', ' ')}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Journal Entry Lines */}
                  {transactionDetail.journal_entry_lines && transactionDetail.journal_entry_lines.length > 0 ? (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Journal Entry Lines</h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Account</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Description</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Debit</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Credit</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {transactionDetail.journal_entry_lines
                              .sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0))
                              .map((line: any) => (
                              <tr key={line.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-4 py-3">
                                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                                    {line.chart_of_accounts ? (
                                      <span className="text-blue-600 dark:text-blue-400">
                                        {line.chart_of_accounts.account_code} - {line.chart_of_accounts.account_name}
                                      </span>
                                    ) : (
                                      'Unknown Account'
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  {line.description && (
                                    <div className="text-sm text-gray-900 dark:text-white">{line.description}</div>
                                  )}
                                  {line.narration && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">{line.narration}</div>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-right text-sm text-gray-900 dark:text-white">
                                  {line.debit_amount > 0 ? formatCurrency(line.debit_amount) : '-'}
                                </td>
                                <td className="px-4 py-3 text-right text-sm text-gray-900 dark:text-white">
                                  {line.credit_amount > 0 ? formatCurrency(line.credit_amount) : '-'}
                                </td>
                              </tr>
                            ))}
                            <tr className="bg-gray-50 dark:bg-gray-700 font-semibold">
                              <td colSpan={2} className="px-4 py-3 text-sm text-gray-900 dark:text-white">Total</td>
                              <td className="px-4 py-3 text-right text-sm text-gray-900 dark:text-white">
                                {formatCurrency(transactionDetail.total_debit || 0)}
                              </td>
                              <td className="px-4 py-3 text-right text-sm text-gray-900 dark:text-white">
                                {formatCurrency(transactionDetail.total_credit || 0)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      No journal entry lines found
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p className="text-lg mb-2">Unable to load transaction details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Accounting
